import { useState, useMemo, useCallback, useEffect } from "react";
import { usePagination } from "@/hooks/usePagination";
import { useCountedServerPagination, useServerPagination } from "@/hooks/useServerPagination";
import { PermissionGate } from "@/components/PermissionGate";
import { PaginationBar } from "@/components/PaginationBar";
import {
  useWarehouseInventory,
  type ReceivingRecord,
  type JobAllocation,
  type DiscrepancyType,
  type Shipment,
  type ShipmentStatus,
} from "@/data/WarehouseInventoryContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
  Package, Search, AlertTriangle, CheckCircle2, Boxes, Truck,
  ArrowRight, ClipboardCheck, ShieldCheck, PackageOpen,
  ChevronDown, ChevronRight, XCircle, FileUp, History, Download, Loader2, ShoppingBag,
  ArrowLeftRight, Warehouse, Pencil, Power, Plus,
} from "lucide-react";
import { formatShortDate, formatStandardTimestamp } from "@/lib/dateFormat";
import { useActionPermissions } from "@/hooks/useActionPermissions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { classifySku, type SkuStatus } from "@/data/inventory/skuValidation";
import { useAppData } from "@/data/AppDataContext";
import type { Job, MaterialLineItem, UnmappedSkuReviewItem } from "@/data/types";
import { getAllocationCoverage } from "@/data/inventory/allocationValidation";
import {
  createProduct,
  getProducts,
  logInventoryCsvImportAudit,
  updateProductStockBySku,
  type InventoryCsvImportMode,
} from "@/services/productRegistry.service";
import { useProductRegistry, type RegistryProduct, type ProductCategory } from "@/data/ProductRegistryContext";
import { JobService } from "@/services/job.service";
import { supabase } from "@/lib/supabase";
import { WAREHOUSE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { WarehouseService } from "@/services/warehouse.service";
import { repairStockFromSourceRecords } from "@/services/inventory.service";
import { WarehousePageService, type WarehouseAllocationLane } from "@/services/warehouse-page.service";
import { WarehouseTransferService, type WarehouseTransfer, type WarehouseStockSummary, type WarehouseStockByLocation } from "@/services/warehouse-transfer.service";
import { WarehouseLocationsService, type Warehouse } from "@/services/warehouse-locations.service";
import { TransferRequestModal } from "@/components/warehouse/TransferRequestModal";
import { WarehouseMapPicker, type WarehousePin } from "@/components/warehouse/WarehouseMapPicker";
import {
  useWarehouseAllocationsPage,
  useWarehouseJobMaterialAllocationPage,
  useWarehousePendingSkuApprovalsPage,
  useWarehouseReadinessPage,
  useWarehouseRealtimeRefreshSignal,
  useWarehouseReceivingPage,
  useWarehouseShipmentsPage,
  useWarehouseStockPage,
  useWarehouseSummary,
} from "@/hooks/useWarehouseServerPaging";

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function toLocalDateInputValue(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function localDateInputToIso(value: string): string | null {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parts = normalized.split("-").map((p) => Number(p));
  if (parts.length !== 3 || parts.some((p) => !Number.isFinite(p))) return null;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function getProductLabel(job: Job): string {
  const pc = job.projectConfig;
  const clean = (value: unknown): string => {
    const v = String(value ?? "").trim();
    if (!v || isUuidLike(v)) return "";
    return v;
  };
  if (!pc) return clean(job.projectType) || "Project";
  const mat = clean(pc.primaryMaterial) || clean(pc.selectedWallSystem);
  const sys = clean(pc.bathroomSystemType) || clean(pc.autoProjectType) || clean(job.projectType);
  if (mat && sys && mat !== sys) return `${mat} ${sys}`;
  return mat || sys || "Project";
}

/** Trim legacy long-form PO numbers (PO-20260610235413-F8SM → PO-0610-F8SM). */
function formatPoNumber(po: string | undefined | null): string {
  if (!po) return "—";
  // Old format: PO-YYYYMMDDHHMMSS-XXXX  →  extract MMDD + random suffix
  const legacy = po.match(/^PO-(\d{4})(\d{2})(\d{2})\d{6}-([A-Z0-9]+)$/i);
  if (legacy) return `PO-${legacy[2]}${legacy[3]}-${legacy[4].toUpperCase()}`;
  return po;
}

/* ─── SKU Status Badge (shared between Stock + Receiving tabs) ─── */
function SkuStatusBadge({ status }: { status: SkuStatus }) {
  if (status === "canonical") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className="text-[9px] bg-primary/10 text-primary border-primary/30">Canonical</Badge>
        </TooltipTrigger>
        <TooltipContent>Recognized by a canonical JobSpec BOM / approved catalog.</TooltipContent>
      </Tooltip>
    );
  }
  if (status === "imported") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className="text-[9px] bg-accent/15 text-accent-foreground border-accent/30">Imported</Badge>
        </TooltipTrigger>
        <TooltipContent>Real migrated inventory SKU. Not yet mapped to a JobSpec BOM.</TooltipContent>
      </Tooltip>
    );
  }
  if (status === "deprecated") {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className="text-[9px] border-muted-foreground/40 text-muted-foreground">Deprecated</Badge>
        </TooltipTrigger>
        <TooltipContent>Old demo / seed SKU. Kept for historical fixtures only — not for future use.</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className="text-[9px] bg-warning/15 text-warning border-warning/30">Unmapped</Badge>
      </TooltipTrigger>
      <TooltipContent>Unknown SKU with no canonical, imported, or deprecated match.</TooltipContent>
    </Tooltip>
  );
}

/* ─── Shipment Receiving Dialog ─── */
function ShipmentReceivingDialog({ shipment, open, onOpenChange, onConfirm }: {
  shipment: Shipment | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (shipmentId: string, items: { sku: string; received: number; discrepancy?: { type: DiscrepancyType; notes: string } }[]) => void | Promise<void>;
}) {
  const [itemStates, setItemStates] = useState<Record<string, {
    qty: number; hasDisc: boolean; discType: DiscrepancyType; discNotes: string;
  }>>({});
  const [confirming, setConfirming] = useState(false);
  const [partialAcknowledged, setPartialAcknowledged] = useState(false);

  const handleOpen = (o: boolean) => {
    if (o && shipment) {
      const init: typeof itemStates = {};
      for (const item of shipment.items) {
        const remaining = item.expectedQuantity - item.receivedQuantity;
        if (remaining > 0) {
          init[item.sku + item.jobId] = { qty: remaining, hasDisc: false, discType: "Quantity Mismatch", discNotes: "" };
        }
      }
      setItemStates(init);
      setPartialAcknowledged(false);
    }
    onOpenChange(o);
  };

  if (!shipment) return null;

  const pendingItems = shipment.items.filter(i => i.receivedQuantity < i.expectedQuantity);
  const partialItems = pendingItems.filter((item) => {
    const key = item.sku + item.jobId;
    const state = itemStates[key];
    const remaining = item.expectedQuantity - item.receivedQuantity;
    return state && state.qty > 0 && state.qty < remaining;
  });
  const hasPartial = partialItems.length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" /> Receive Shipment — {shipment.vendorName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-muted/50 rounded-md p-3 flex items-center justify-between">
            <div>
              <p className="text-xs"><span className="text-muted-foreground">PO:</span> <span className="font-mono font-medium">{formatPoNumber(shipment.poNumber)}</span></p>
              <p className="text-xs text-muted-foreground">{shipment.items.length} item(s)</p>
            </div>
            <Badge variant="outline" className="text-[10px]">{shipment.status}</Badge>
          </div>

          {pendingItems.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">All items already received</p>
          ) : (
            pendingItems.map((item) => {
              const key = item.sku + item.jobId;
              const state = itemStates[key] || { qty: 0, hasDisc: false, discType: "Quantity Mismatch" as DiscrepancyType, discNotes: "" };
              const remaining = item.expectedQuantity - item.receivedQuantity;
              return (
                <Card key={key} className="border">
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">{item.productName}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{item.sku} · {item.jobLabel}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs">{item.receivedQuantity}/{item.expectedQuantity}</p>
                        <p className="text-[10px] text-muted-foreground">{remaining} remaining</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <Label className="text-[10px]">Qty Received</Label>
                        <Input type="number" className="h-7 text-xs" min={0} max={remaining}
                          value={state.qty}
                          onChange={(e) => setItemStates(prev => ({ ...prev, [key]: { ...state, qty: Math.min(remaining, Math.max(0, parseInt(e.target.value) || 0)) } }))}
                        />
                      </div>
                      <div className="flex items-center gap-1.5 pt-4">
                        <input type="checkbox" checked={state.hasDisc}
                          onChange={(e) => setItemStates(prev => ({ ...prev, [key]: { ...state, hasDisc: e.target.checked } }))}
                          className="rounded"
                        />
                        <Label className="text-[10px]">Discrepancy</Label>
                      </div>
                    </div>
                    {state.hasDisc && (
                      <div className="space-y-1.5 border border-destructive/30 rounded p-2 bg-destructive/5">
                        <Select value={state.discType} onValueChange={(v) => setItemStates(prev => ({ ...prev, [key]: { ...state, discType: v as DiscrepancyType } }))}>
                          <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Missing">Missing</SelectItem>
                            <SelectItem value="Damaged">Damaged</SelectItem>
                            <SelectItem value="Wrong Item">Wrong Item</SelectItem>
                            <SelectItem value="Quantity Mismatch">Quantity Mismatch</SelectItem>
                          </SelectContent>
                        </Select>
                        <Textarea className="text-[10px]" rows={1} placeholder="Notes..."
                          value={state.discNotes}
                          onChange={(e) => setItemStates(prev => ({ ...prev, [key]: { ...state, discNotes: e.target.value } }))}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
        {hasPartial && (
          <div className="rounded-md border border-warning/40 bg-warning/10 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-warning">Partial receive — {partialItems.length} item(s) below expected quantity</p>
                <ul className="mt-1 space-y-0.5">
                  {partialItems.map((item) => {
                    const key = item.sku + item.jobId;
                    const state = itemStates[key];
                    const remaining = item.expectedQuantity - item.receivedQuantity;
                    return (
                      <li key={key} className="text-[10px] text-muted-foreground">
                        <span className="font-mono">{item.sku}</span> — receiving {state?.qty ?? 0} of {remaining} remaining
                      </li>
                    );
                  })}
                </ul>
                <p className="text-[10px] text-muted-foreground mt-1.5">This job will stay as <strong>Partially Received</strong> and will <strong>not appear on Load-Out</strong> until all materials are fully received.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="partial-ack"
                checked={partialAcknowledged}
                onChange={(e) => setPartialAcknowledged(e.target.checked)}
                className="rounded"
              />
              <label htmlFor="partial-ack" className="text-[10px] cursor-pointer">I understand this is a partial receive</label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={pendingItems.length === 0 || confirming || (hasPartial && !partialAcknowledged)} onClick={async () => {
            const receipts = pendingItems.map((item) => {
              const key = item.sku + item.jobId;
              const state = itemStates[key];
              return {
                sku: item.sku,
                received: state?.qty || 0,
                ...(state?.hasDisc ? { discrepancy: { type: state.discType, notes: state.discNotes } } : {}),
              };
            }).filter(r => r.received > 0);
            setConfirming(true);
            try {
              await onConfirm(shipment.id, receipts);
              onOpenChange(false);
              toast.success(`Shipment from ${shipment.vendorName} received`);
            } finally {
              setConfirming(false);
            }
          }}>
            {confirming ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Confirming…</> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Confirm Receipt</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Single Item Receiving Dialog ─── */
function ReceivingDialog({ record, open, onOpenChange, onConfirm }: {
  record: ReceivingRecord | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: (id: string, qty: number, disc?: { type: DiscrepancyType; notes: string }) => void | Promise<void>;
}) {
  const [qty, setQty] = useState(0);
  const [hasDiscrepancy, setHasDiscrepancy] = useState(false);
  const [discType, setDiscType] = useState<DiscrepancyType>("Quantity Mismatch");
  const [discNotes, setDiscNotes] = useState("");
  const [confirming, setConfirming] = useState(false);

  const handleOpen = (o: boolean) => {
    if (o && record) {
      setQty(record.expectedQuantity - record.receivedQuantity);
      setHasDiscrepancy(false); setDiscNotes("");
    }
    onOpenChange(o);
  };

  if (!record) return null;
  const remaining = record.expectedQuantity - record.receivedQuantity;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Confirm Receiving</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-muted/50 rounded-md p-3 space-y-1">
            <p className="text-xs"><span className="text-muted-foreground">PO:</span> <span className="font-mono font-medium">{formatPoNumber(record.poNumber)}</span></p>
            <p className="text-xs"><span className="text-muted-foreground">Vendor:</span> {record.vendorName}</p>
            <p className="text-xs"><span className="text-muted-foreground">Job:</span> {record.jobLabel}</p>
            <p className="text-xs"><span className="text-muted-foreground">SKU:</span> <span className="font-mono">{record.sku}</span></p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-muted/30 rounded p-2"><p className="text-lg font-bold">{record.expectedQuantity}</p><p className="text-[10px] text-muted-foreground">Expected</p></div>
            <div className="bg-muted/30 rounded p-2"><p className="text-lg font-bold">{record.receivedQuantity}</p><p className="text-[10px] text-muted-foreground">Received</p></div>
            <div className="bg-primary/10 rounded p-2"><p className="text-lg font-bold">{remaining}</p><p className="text-[10px] text-muted-foreground">Remaining</p></div>
          </div>
          <div>
            <Label className="text-xs">Quantity Received Now</Label>
            <Input type="number" className="h-8 text-xs" min={0} max={remaining} value={qty} onChange={(e) => setQty(Math.min(remaining, Math.max(0, parseInt(e.target.value) || 0)))} />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={hasDiscrepancy} onChange={(e) => setHasDiscrepancy(e.target.checked)} className="rounded" />
            <Label className="text-xs">Report Discrepancy</Label>
          </div>
          {hasDiscrepancy && (
            <div className="space-y-2 border border-destructive/30 rounded-md p-3 bg-destructive/5">
              <Select value={discType} onValueChange={(v) => setDiscType(v as DiscrepancyType)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Missing">Missing</SelectItem>
                  <SelectItem value="Damaged">Damaged</SelectItem>
                  <SelectItem value="Wrong Item">Wrong Item</SelectItem>
                  <SelectItem value="Quantity Mismatch">Quantity Mismatch</SelectItem>
                </SelectContent>
              </Select>
              <Textarea className="text-xs" rows={2} value={discNotes} onChange={(e) => setDiscNotes(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <PermissionGate permission="warehouse_edit">
            <Button onClick={async () => {
              setConfirming(true);
              try {
                await onConfirm(record.id, qty, hasDiscrepancy ? { type: discType, notes: discNotes } : undefined);
                onOpenChange(false);
                toast.success(`${qty} unit(s) of ${record.sku} received`);
              } finally {
                setConfirming(false);
              }
            }} disabled={qty === 0 || confirming}>
              {confirming ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Confirming…</> : <><CheckCircle2 className="h-3 w-3 mr-1" /> Confirm Receipt</>}
            </Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Re-Allocation Modal ─── */
function ReAllocationModal({ open, onOpenChange, allocation, onConfirm }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  allocation: JobAllocation | null;
  onConfirm: (destJobId: string, destJobLabel: string, qty: number) => Promise<void>;
}) {
  const [qty, setQty] = useState(1);
  const [destJobId, setDestJobId] = useState("");
  const [destJobLabel, setDestJobLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [activeJobs, setActiveJobs] = useState<{ id: string; label: string; customerName: string }[]>([]);
  const [existingDestQty, setExistingDestQty] = useState(0);

  // Load active jobs when modal opens
  useEffect(() => {
    if (!open || !allocation) return;
    setQty(allocation.quantity);
    setDestJobId("");
    setDestJobLabel("");
    setJobSearch("");
    setActiveJobs([]);
    setExistingDestQty(0);
    void (async () => {
      const { data } = await supabase
        .from("jobs")
        .select("id, job_number, title")
        .neq("current_stage", "Completed")
        .neq("id", allocation.jobId)
        .order("job_number", { ascending: true })
        .limit(200);
      setActiveJobs(
        (data ?? []).map((r) => {
          const raw = r as { id?: string | null; job_number?: string | null; title?: string | null };
          const label = String(raw.job_number ?? raw.id ?? "");
          const customerName = String(raw.title ?? "");
          return { id: String(raw.id ?? ""), label, customerName };
        }).filter((j) => j.id),
      );
    })();
  }, [open, allocation]);

  // Check existing allocation for selected destination job
  useEffect(() => {
    if (!destJobId || !allocation) { setExistingDestQty(0); return; }
    void (async () => {
      const { data } = await supabase
        .from("job_allocations")
        .select("quantity, status")
        .eq("job_id", destJobId)
        .eq("sku", allocation.sku)
        .in("status", ["Reserved", "Ready for Install"]);
      const total = (data ?? []).reduce((sum, r) => sum + Math.max(0, Number((r as { quantity?: number | null }).quantity ?? 0)), 0);
      setExistingDestQty(total);
    })();
  }, [destJobId, allocation]);

  if (!allocation) return null;

  const filteredJobs = jobSearch.trim()
    ? activeJobs.filter((j) =>
        j.label.toLowerCase().includes(jobSearch.toLowerCase()) ||
        j.customerName.toLowerCase().includes(jobSearch.toLowerCase()),
      )
    : activeJobs;

  const canConfirm = destJobId !== "" && qty >= 1 && qty <= allocation.quantity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-1.5">
            <ArrowLeftRight className="h-3.5 w-3.5" />
            Re-Allocate — {allocation.productName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Source info */}
          <div className="bg-muted/50 rounded-md p-3 space-y-1">
            <p className="text-xs"><span className="text-muted-foreground">From job:</span> <span className="font-medium">{allocation.jobLabel || allocation.jobId}</span></p>
            <p className="text-xs"><span className="text-muted-foreground">SKU:</span> <span className="font-mono">{allocation.sku}</span></p>
            <p className="text-xs"><span className="text-muted-foreground">Allocated qty:</span> <span className="font-mono tabular-nums">{allocation.quantity}</span></p>
          </div>

          {/* Qty to move */}
          <div>
            <Label className="text-xs">Qty to Move</Label>
            <Input
              type="number"
              className="h-8 text-xs"
              min={1}
              max={allocation.quantity}
              value={qty}
              onChange={(e) => {
                const val = e.target.valueAsNumber;
                if (!isNaN(val) && val >= 1) setQty(Math.min(allocation.quantity, Math.floor(val)));
              }}
            />
            {qty < allocation.quantity && (
              <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">
                {allocation.quantity - qty} will remain on {allocation.jobLabel || "source job"}
              </p>
            )}
          </div>

          {/* Destination job */}
          <div>
            <Label className="text-xs">Destination Job</Label>
            <Input
              placeholder="Search by job label or homeowner…"
              className="h-8 text-xs mb-1"
              value={jobSearch}
              onChange={(e) => setJobSearch(e.target.value)}
            />
            <Select
              value={destJobId}
              onValueChange={(id) => {
                setDestJobId(id);
                const found = activeJobs.find((j) => j.id === id);
                setDestJobLabel(found?.label ?? id);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select destination job…" />
              </SelectTrigger>
              <SelectContent>
                {filteredJobs.length === 0 && (
                  <SelectItem value="__none__" disabled>No jobs found</SelectItem>
                )}
                {filteredJobs.map((j) => (
                  <SelectItem key={j.id} value={j.id} className="text-xs">
                    {j.label}{j.customerName ? ` — ${j.customerName}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Existing allocation note */}
          {existingDestQty > 0 && (
            <div className="rounded-md border border-info/30 bg-info/5 px-3 py-2">
              <p className="text-[10px] text-info">
                {destJobLabel || "That job"} already has <span className="font-mono tabular-nums font-semibold">{existingDestQty}</span> × {allocation.sku} allocated — your <span className="font-mono tabular-nums font-semibold">{qty}</span> will add to that, totalling <span className="font-mono tabular-nums font-semibold">{existingDestQty + qty}</span>.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <PermissionGate permission="warehouse_edit">
            <Button
              disabled={!canConfirm || busy}
              onClick={async () => {
                try {
                  setBusy(true);
                  await onConfirm(destJobId, destJobLabel, qty);
                  onOpenChange(false);
                } catch (err) {
                  toast.error(`Re-allocation failed: ${err instanceof Error ? err.message : String(err)}`);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Moving…</> : <><ArrowLeftRight className="h-3 w-3 mr-1" /> Re-Allocate</>}
            </Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Partial Allocate Dialog (Received — Needs Allocation) ─── */
function PartialAllocateDialog({ open, onOpenChange, record, onAllocate, requiredQty, alreadyAllocated }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  record: ReceivingRecord | null;
  onAllocate: (sku: string, jobId: string, jobLabel: string, qty: number, productName?: string) => Promise<JobAllocation | null>;
  requiredQty?: number;
  alreadyAllocated?: number;
}) {
  const receivedQty = record ? (record.receivedQuantity ?? record.expectedQuantity ?? 1) : 1;
  const remaining = (requiredQty != null && alreadyAllocated != null)
    ? Math.max(0, requiredQty - alreadyAllocated)
    : receivedQty;
  const maxQty = remaining > 0 ? remaining : receivedQty;
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setQty(Math.max(1, Math.min(maxQty, receivedQty)));
  }, [open, record]);

  if (!record) return null;

  const hasBomContext = requiredQty != null && alreadyAllocated != null;
  const isPartial = hasBomContext && qty < remaining;
  const isFull = hasBomContext && qty >= remaining;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="text-sm">Allocate to Job</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-xs"><span className="text-muted-foreground">SKU:</span> <span className="font-mono">{record.sku}</span></p>
            <p className="text-xs"><span className="text-muted-foreground">Product:</span> {record.productName}</p>
            <p className="text-xs"><span className="text-muted-foreground">Job:</span> {record.jobLabel}</p>
          </div>
          {hasBomContext && (
            <div className="bg-muted/30 rounded-md p-3 space-y-1 text-xs tabular-nums">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Required:</span>
                <span className="font-mono">{requiredQty}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Already allocated:</span>
                <span className="font-mono">{alreadyAllocated}</span>
              </div>
              <div className="flex justify-between font-medium border-t pt-1 mt-1">
                <span className="text-muted-foreground">Remaining needed:</span>
                <span className="font-mono">{remaining}</span>
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs">Quantity to Allocate</Label>
            <Input
              type="number"
              className="h-8 text-xs"
              min={1}
              value={qty}
              onChange={(e) => {
                const val = e.target.valueAsNumber;
                if (!isNaN(val) && val >= 1) setQty(Math.floor(val));
              }}
            />
          </div>
          {hasBomContext && isPartial && (
            <Badge className="text-[10px] bg-warning/15 text-warning border-warning/30">
              Partial allocation — {qty} of {remaining} remaining
            </Badge>
          )}
          {hasBomContext && isFull && (
            <Badge className="text-[10px] bg-success/15 text-success border-success/30">
              Fully covers remaining need
            </Badge>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <PermissionGate permission="warehouse_edit">
            <Button disabled={busy} onClick={async () => {
              try {
                setBusy(true);
                const allocation = await onAllocate(record.sku, record.jobId, record.jobLabel, qty, record.productName);
                if (!allocation) {
                  toast.message(`${record.sku} was not allocated. It may already be fully allocated for this job.`);
                  return;
                }
                onOpenChange(false);
                toast.success(`${allocation.quantity} × ${record.sku} allocated to ${record.jobLabel || record.jobId}`);
              } catch (err) {
                toast.error(`Allocation failed: ${err instanceof Error ? err.message : String(err)}`);
              } finally {
                setBusy(false);
              }
            }}>
              <ArrowRight className="h-3 w-3 mr-1" /> Allocate
            </Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Job Material Allocate Dialog (Allocate from Job Materials table) ─── */
function JobMaterialAllocateDialog({ open, onOpenChange, row, onAllocate }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  row: JobMaterialAllocationRow | null;
  onAllocate: (row: JobMaterialAllocationRow, qty: number) => Promise<void>;
}) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && row) setQty(Math.max(1, row.recommendedQuantity));
  }, [open, row]);

  if (!row) return null;

  const isPartial = qty < row.missingQuantity;
  const isFull = qty >= row.missingQuantity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="text-sm">Allocate Material to Job</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="bg-muted/50 rounded-md p-3">
            <p className="text-xs"><span className="text-muted-foreground">SKU:</span> <span className="font-mono">{row.sku}</span></p>
            <p className="text-xs"><span className="text-muted-foreground">Product:</span> {row.productName}</p>
            <p className="text-xs"><span className="text-muted-foreground">Job:</span> {row.jobLabel}</p>
          </div>
          <div className="bg-muted/30 rounded-md p-3 space-y-1 text-xs tabular-nums">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Required:</span>
              <span className="font-mono">{row.requiredQuantity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Already allocated:</span>
              <span className="font-mono">{row.allocatedQuantity}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Available stock:</span>
              <span className={`font-mono ${row.availableStock <= 0 ? "text-warning" : "text-success"}`}>{row.availableStock}</span>
            </div>
            <div className="flex justify-between font-medium border-t pt-1 mt-1">
              <span className="text-muted-foreground">Still needed:</span>
              <span className="font-mono">{row.missingQuantity}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs">Quantity to Allocate</Label>
            <Input
              type="number"
              className="h-8 text-xs"
              min={1}
              value={qty}
              onChange={(e) => {
                const val = e.target.valueAsNumber;
                if (!isNaN(val) && val >= 1) setQty(Math.floor(val));
              }}
            />
          </div>
          {isPartial && (
            <Badge className="text-[10px] bg-warning/15 text-warning border-warning/30">
              Partial — {qty} of {row.missingQuantity} needed
            </Badge>
          )}
          {isFull && (
            <Badge className="text-[10px] bg-success/15 text-success border-success/30">
              Fully covers remaining need
            </Badge>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <PermissionGate permission="warehouse_edit">
            <Button disabled={busy || qty < 1} onClick={async () => {
              try {
                setBusy(true);
                await onAllocate(row, qty);
                onOpenChange(false);
              } catch (err) {
                toast.error(`Allocation failed: ${err instanceof Error ? err.message : String(err)}`);
              } finally {
                setBusy(false);
              }
            }}>
              <ArrowRight className="h-3 w-3 mr-1" /> Allocate {qty}
            </Button>
          </PermissionGate>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Shipment Status Colors ─── */
const SHIPMENT_STATUS_STYLE: Record<ShipmentStatus, string> = {
  "Ordered": "bg-muted text-muted-foreground",
  "In Transit": "bg-info/15 text-info dark:bg-info/30 dark:text-info",
  "Delivered": "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning",
  "Received": "bg-success/15 text-success dark:bg-success/30 dark:text-success",
};

const NEXT_STATUS: Partial<Record<ShipmentStatus, ShipmentStatus>> = {
  "Ordered": "In Transit",
  "In Transit": "Delivered",
};

interface PendingSkuApproval extends UnmappedSkuReviewItem {
  reviewId?: string;
  jobId: string;
  jobLabel: string;
  key: string;
}

type InventoryImportAction = "created" | "updated" | "unchanged" | "invalid";

interface InventoryCsvPreviewRow {
  rowNumber: number;
  sku: string;
  productName: string;
  vendor: string;
  category: ProductCategory;
  subcategory: string;
  unitOfMeasure: string;
  cost: number;
  price: number;
  reorderThreshold: number;
  notes: string;
  importedQuantity: number;
  previousQuantity: number;
  nextQuantity: number;
  action: InventoryImportAction;
  error?: string;
  duplicateCount?: number;
  matchType?: "sku" | "product_name" | "manual";
  matchNote?: string;
  matchCandidates?: string[];
}

interface InventoryCsvPreview {
  fileName: string;
  totalRows: number;
  rows: InventoryCsvPreviewRow[];
}

interface JobMaterialAllocationRow {
  key: string;
  jobId: string;
  jobLabel: string;
  customerName: string;
  sku: string;
  productName: string;
  requiredQuantity: number;
  allocatedQuantity: number;
  missingQuantity: number;
  availableStock: number;
  recommendedQuantity: number;
  hasStockMatch: boolean;
}

function withServerRows<T>(controls: ReturnType<typeof useServerPagination>, paged: T[]) {
  return { ...controls, paged };
}

const CSV_HEADER_ALIASES = {
  sku: ["sku", "item sku", "product sku", "product_sku", "internal sku", "internal_sku"],
  quantity: ["quantity", "qty", "stock", "stock quantity", "stock_quantity", "on hand", "on_hand", "current stock", "current_stock"],
  productName: ["product", "product name", "product_name", "item", "item name", "item_name", "name", "description"],
  vendor: ["vendor", "supplier", "manufacturer"],
  category: ["category", "type"],
  subcategory: ["subcategory", "sub category", "sub_category"],
  unitOfMeasure: ["unit", "uom", "unit of measure", "unit_of_measure"],
  cost: ["cost", "unit cost", "unit_cost"],
  price: ["price", "unit price", "unit_price"],
  reorderThreshold: ["reorder", "reorder at", "reorder_at", "reorder threshold", "reorder_threshold", "threshold"],
  notes: ["notes", "note", "memo"],
} as const;

const PRODUCT_CATEGORY_SET = new Set<ProductCategory>([
  "Wall System", "Base", "Glass/Enclosure", "Hardware", "Add-On", "Other",
]);

function pendingSkuApprovalKey(jobId: string, item: UnmappedSkuReviewItem): string {
  return `${jobId}:${item.sku}:${item.itemName}`;
}

function normalizeWarehouseSku(value: string): string {
  return value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

function resolveWarehouseSkuStatus(
  sku: string,
  registrySkuSet: ReadonlySet<string>,
): SkuStatus {
  const fallback = classifySku(sku);
  if (fallback !== "unmapped") return fallback;
  return registrySkuSet.has(normalizeWarehouseSku(sku)) ? "canonical" : "unmapped";
}

function normalizeProductNameForMatch(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function findProductByName(
  products: RegistryProduct[],
  productName: string,
): { product: RegistryProduct | null; candidates: RegistryProduct[] } {
  const normalizedName = normalizeProductNameForMatch(productName);
  if (!normalizedName) return { product: null, candidates: [] };

  const candidates = products.filter((product) =>
    normalizeProductNameForMatch(product.productName) === normalizedName
  );

  return {
    product: candidates.length === 1 ? candidates[0] : null,
    candidates,
  };
}

function removePendingSkuReview(
  items: Job["unmappedSkusForReview"] | undefined,
  jobId: string,
  key: string,
): UnmappedSkuReviewItem[] {
  return (items ?? []).filter((item) => pendingSkuApprovalKey(jobId, item) !== key);
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    const next = text[i + 1];
    if (char === '"' && inQuotes && next === '"') {
      cell += '"';
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (char === "," && !inQuotes) {
      row.push(cell.trim());
      cell = "";
      continue;
    }
    if ((char === "\n" || char === "\r") && !inQuotes) {
      if (char === "\r" && next === "\n") i += 1;
      row.push(cell.trim());
      if (row.some((value) => value.length > 0)) rows.push(row);
      row = [];
      cell = "";
      continue;
    }
    cell += char;
  }

  row.push(cell.trim());
  if (row.some((value) => value.length > 0)) rows.push(row);
  return rows;
}

function normalizeCsvHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

function resolveCsvColumn(headers: string[], aliases: readonly string[]): number {
  const normalizedAliases = aliases.map(normalizeCsvHeader);
  return headers.findIndex((header) => normalizedAliases.includes(normalizeCsvHeader(header)));
}

function readCsvValue(row: string[], headers: string[], aliases: readonly string[]): string {
  const index = resolveCsvColumn(headers, aliases);
  return index >= 0 ? String(row[index] ?? "").trim() : "";
}

function parseCsvNumber(value: string, fallback = 0): number {
  const cleaned = String(value || "").replace(/[$,\s]/g, "");
  if (!cleaned) return fallback;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function normalizeInventoryCategory(value: string): ProductCategory {
  const trimmed = value.trim();
  if (PRODUCT_CATEGORY_SET.has(trimmed as ProductCategory)) return trimmed as ProductCategory;
  const lowered = trimmed.toLowerCase();
  if (lowered.includes("wall")) return "Wall System";
  if (lowered.includes("base") || lowered.includes("pan")) return "Base";
  if (lowered.includes("glass") || lowered.includes("door") || lowered.includes("enclosure")) return "Glass/Enclosure";
  if (lowered.includes("hardware") || lowered.includes("fixture")) return "Hardware";
  if (lowered.includes("add")) return "Add-On";
  return "Other";
}

function buildInventoryCsvPreview(
  text: string,
  fileName: string,
  mode: InventoryCsvImportMode,
  products: RegistryProduct[],
): InventoryCsvPreview {
  const rows = parseCsvText(text);
  if (rows.length === 0) return { fileName, totalRows: 0, rows: [] };

  const headers = rows[0];
  const dataRows = rows.slice(1);
  const existingBySku = new Map(products.map((product) => [normalizeWarehouseSku(product.sku), product]));
  const grouped = new Map<string, InventoryCsvPreviewRow>();
  const invalidRows: InventoryCsvPreviewRow[] = [];

  dataRows.forEach((row, index) => {
    const rowNumber = index + 2;
    const rawSku = readCsvValue(row, headers, CSV_HEADER_ALIASES.sku);
    const rawName = readCsvValue(row, headers, CSV_HEADER_ALIASES.productName);
    const nameMatch = !rawSku && rawName ? findProductByName(products, rawName) : null;
    const matchedByNameProduct = nameMatch?.product ?? null;
    const sku = normalizeWarehouseSku(rawSku || matchedByNameProduct?.sku || "");
    const importedQuantity = parseCsvNumber(readCsvValue(row, headers, CSV_HEADER_ALIASES.quantity));
    const matchCandidates = nameMatch?.candidates.map((product) => product.sku).filter(Boolean) ?? [];

    if (!sku || !Number.isFinite(importedQuantity) || importedQuantity < 0) {
      invalidRows.push({
        rowNumber,
        sku: sku || "—",
        productName: rawName || "—",
        vendor: "",
        category: "Other",
        subcategory: "",
        unitOfMeasure: "each",
        cost: 0,
        price: 0,
        reorderThreshold: 0,
        notes: "",
        importedQuantity: Number.isFinite(importedQuantity) ? importedQuantity : 0,
        previousQuantity: 0,
        nextQuantity: 0,
        action: "invalid",
        error: !sku
          ? rawName
            ? matchCandidates.length > 1
              ? "Product name matches multiple SKUs. Choose the SKU manually."
              : "Missing SKU. No exact product name match; choose the SKU manually."
            : "Missing SKU or product name"
          : "Quantity must be a non-negative number",
        matchCandidates,
      });
      return;
    }

    const existing = existingBySku.get(sku);
    const productName = rawName || existing?.productName || sku;
    const current = grouped.get(sku);
    const nextImportedQuantity = mode === "add"
      ? (current?.importedQuantity ?? 0) + importedQuantity
      : importedQuantity;
    const previousQuantity = Number(existing?.stockQuantity || 0);
    const nextQuantity = mode === "add"
      ? previousQuantity + nextImportedQuantity
      : nextImportedQuantity;

    grouped.set(sku, {
      rowNumber: current?.rowNumber ?? rowNumber,
      sku,
      productName,
      vendor: readCsvValue(row, headers, CSV_HEADER_ALIASES.vendor) || current?.vendor || existing?.vendor || "CSV Import",
      category: normalizeInventoryCategory(readCsvValue(row, headers, CSV_HEADER_ALIASES.category) || current?.category || existing?.category || "Other"),
      subcategory: readCsvValue(row, headers, CSV_HEADER_ALIASES.subcategory) || current?.subcategory || existing?.subcategory || "CSV Import",
      unitOfMeasure: readCsvValue(row, headers, CSV_HEADER_ALIASES.unitOfMeasure) || current?.unitOfMeasure || existing?.unitOfMeasure || "each",
      cost: parseCsvNumber(readCsvValue(row, headers, CSV_HEADER_ALIASES.cost), current?.cost ?? existing?.cost ?? 0),
      price: parseCsvNumber(readCsvValue(row, headers, CSV_HEADER_ALIASES.price), current?.price ?? existing?.price ?? 0),
      reorderThreshold: parseCsvNumber(
        readCsvValue(row, headers, CSV_HEADER_ALIASES.reorderThreshold),
        current?.reorderThreshold ?? existing?.reorderThreshold ?? 0,
      ),
      notes: readCsvValue(row, headers, CSV_HEADER_ALIASES.notes) || current?.notes || existing?.notes || "",
      importedQuantity: nextImportedQuantity,
      previousQuantity,
      nextQuantity,
      action: existing ? (nextQuantity === previousQuantity ? "unchanged" : "updated") : "created",
      duplicateCount: (current?.duplicateCount ?? 0) + 1,
      matchType: rawSku ? "sku" : "product_name",
      matchNote: !rawSku && existing
        ? `Matched by product name to SKU ${existing.sku}`
        : undefined,
    });
  });

  const previewRows = [...grouped.values(), ...invalidRows].sort((a, b) => a.rowNumber - b.rowNumber);
  return { fileName, totalRows: dataRows.length, rows: previewRows };
}

function downloadInventoryCsvTemplate(): void {
  const headers = [
    "sku",
    "quantity",
    "product_name",
    "vendor",
    "category",
    "subcategory",
    "unit_of_measure",
    "cost",
    "price",
    "reorder_threshold",
    "notes",
  ];
  const sample = [
    "SKU-EXAMPLE-001",
    "10",
    "Example Inventory Item",
    "Warehouse",
    "Other",
    "CSV Import",
    "each",
    "0",
    "0",
    "2",
    "Optional import note",
  ];
  const csv = [headers, sample]
    .map((row) => row.map((value) => `"${value.replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "warehouse-inventory-import-template.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function normalizeAllocationSku(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

function materialRequiredQuantity(material: MaterialLineItem): number {
  return Math.max(0, Number(material.quantityRequired || 0));
}

/* ═══════════════════════════════════════════════════
   Main Page
   ═══════════════════════════════════════════════════ */
export default function WarehouseInventoryPage() {
  const { products: contextProducts } = useProductRegistry();
  const [standaloneProducts, setStandaloneProducts] = useState<RegistryProduct[]>([]);
  useEffect(() => {
    if (contextProducts.length === 0) {
      getProducts().then(setStandaloneProducts).catch(console.error);
    }
  }, []);
  const registryProducts = contextProducts.length > 0 ? contextProducts : standaloneProducts;
  const {
    stockRecords, receivingRecords, jobAllocations, shipments,
    confirmReceiving, allocateToJob, markReadyForInstall, markAsInstalled, lowStockItems,
    updateShipmentStatus, receiveShipment, isJobReadyForInstall,
    refreshData: refreshWarehouseData,
  } = useWarehouseInventory();
  const actionPerms = useActionPermissions();

  // ── Transfer state ────────────────────────────────────────────────────────
  const [transfers, setTransfers]                 = useState<WarehouseTransfer[]>([]);
  const [transferCount, setTransferCount]         = useState(0);
  const [transferPage, setTransferPage]           = useState(0);
  const [transfersLoading, setTransfersLoading]   = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [expandedTransferId, setExpandedTransferId] = useState<string | null>(null);
  const TRANSFER_PAGE_SIZE = 20;

  // ── Warehouse stock viewer (Hub tab) ─────────────────────────────────────
  const [viewingStockWarehouseId, setViewingStockWarehouseId] = useState<string | null>(null);
  const [warehouseStockCache, setWarehouseStockCache] = useState<Record<string, WarehouseStockByLocation[]>>({});
  const [warehouseStockLoading, setWarehouseStockLoading] = useState(false);
  const [stockSubTab, setStockSubTab] = useState<string>("all");
  const [stockLocationLoading, setStockLocationLoading] = useState(false);

  async function handleViewWarehouseStock(warehouseId: string) {
    if (viewingStockWarehouseId === warehouseId) {
      setViewingStockWarehouseId(null);
      return;
    }
    setViewingStockWarehouseId(warehouseId);
    if (warehouseStockCache[warehouseId]) return;
    setWarehouseStockLoading(true);
    const items = await WarehouseTransferService.listStockByWarehouse(warehouseId);
    setWarehouseStockCache((prev) => ({ ...prev, [warehouseId]: items }));
    setWarehouseStockLoading(false);
  }

  // ── Warehouse management state ────────────────────────────────────────────
  const [warehouseSummaries, setWarehouseSummaries]   = useState<WarehouseStockSummary[]>([]);
  const [warehousesLoading, setWarehousesLoading]     = useState(false);
  const [addWarehouseOpen, setAddWarehouseOpen]       = useState(false);
  const [newWhName, setNewWhName]                     = useState("");
  const [newWhCode, setNewWhCode]                     = useState("");
  const [addingWarehouse, setAddingWarehouse]         = useState(false);
  const [editingWarehouse, setEditingWarehouse]       = useState<WarehouseStockSummary | null>(null);
  const [editWhName, setEditWhName]                   = useState("");
  const [editWhCode, setEditWhCode]                   = useState("");
  // Location fields (shared for add + edit)
  const [newWhAddress, setNewWhAddress]               = useState("");
  const [newWhLat, setNewWhLat]                       = useState<number | null>(null);
  const [newWhLng, setNewWhLng]                       = useState<number | null>(null);
  const [showAddMap, setShowAddMap]                   = useState(false);
  const [mapGeocoding, setMapGeocoding]               = useState(false);
  const [editWhAddress, setEditWhAddress]             = useState("");
  const [editWhLat, setEditWhLat]                     = useState<number | null>(null);
  const [editWhLng, setEditWhLng]                     = useState<number | null>(null);
  const [showEditMap, setShowEditMap]                 = useState(false);
  const [warehousesList, setWarehousesList]           = useState<Warehouse[]>([]);

  const { jobs, refreshData, systemEvents } = useAppData();
  const registrySkuSet = useMemo(() => {
    const set = new Set<string>();
    for (const product of registryProducts) {
      const normalized = normalizeWarehouseSku(String(product.sku ?? ""));
      if (normalized) set.add(normalized);
    }
    return set;
  }, [registryProducts]);

  const loadTransfers = useCallback(async (page = 0) => {
    setTransfersLoading(true);
    const result = await WarehouseTransferService.list(page, TRANSFER_PAGE_SIZE);
    setTransfers(result.transfers);
    setTransferCount(result.count);
    setTransferPage(page);
    setTransfersLoading(false);
  }, [TRANSFER_PAGE_SIZE]);

  const loadWarehouseSummaries = useCallback(async () => {
    setWarehousesLoading(true);
    const [summaries, whs] = await Promise.all([
      WarehouseTransferService.listSummaries(),
      WarehouseLocationsService.list(),
    ]);
    setWarehouseSummaries(summaries);
    setWarehousesList(whs);
    setWarehousesLoading(false);
  }, []);

  useEffect(() => { loadTransfers(0); }, [loadTransfers]);
  useEffect(() => { loadWarehouseSummaries(); }, [loadWarehouseSummaries]);

  async function handleAddWarehouse() {
    if (!newWhName.trim()) return;
    setAddingWarehouse(true);
    const result = await WarehouseLocationsService.create(
      newWhName,
      newWhCode || undefined,
      newWhAddress || undefined,
      newWhLat,
      newWhLng,
    );
    setAddingWarehouse(false);
    if (result) {
      toast.success(`Warehouse "${result.name}" added`);
      setNewWhName(""); setNewWhCode(""); setNewWhAddress("");
      setNewWhLat(null); setNewWhLng(null); setShowAddMap(false);
      setAddWarehouseOpen(false);
      loadWarehouseSummaries();
    } else {
      toast.error("Failed to add warehouse");
    }
  }

  async function handleSaveEditWarehouse() {
    if (!editingWarehouse || !editWhName.trim()) return;
    const result = await WarehouseLocationsService.update(
      editingWarehouse.warehouseId,
      editWhName,
      editWhCode || undefined,
      editWhAddress || undefined,
      editWhLat,
      editWhLng,
    );
    if (result) {
      toast.success("Warehouse updated");
      setEditingWarehouse(null);
      setShowEditMap(false);
      loadWarehouseSummaries();
    } else {
      toast.error("Failed to update warehouse");
    }
  }

  async function handleDeactivateWarehouse(id: string, name: string) {
    const ok = await WarehouseLocationsService.deactivate(id);
    if (ok) {
      toast.success(`"${name}" deactivated`);
      loadWarehouseSummaries();
    } else {
      toast.error("Failed to deactivate warehouse");
    }
  }

  const jobDisplayById = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) {
      const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
      map.set(job.id, jobNumber || job.id);
    }
    return map;
  }, [jobs]);
  const displayJobRef = useCallback((jobId: string, fallback?: string) => {
    const fb = (fallback ?? "").trim();
    if (fb.length > 0 && !isUuidLike(fb)) return fb;
    const mapped = jobDisplayById.get(jobId);
    if (mapped && mapped.trim().length > 0) return mapped;
    return jobId;
  }, [jobDisplayById]);

  // Normalize job label → canonical UUID so migrated jobs stored by label
  // and by UUID in different tables don't produce duplicate readiness rows.
  const canonicalJobId = useMemo(() => {
    const map = new Map<string, string>();
    for (const job of jobs) {
      map.set(job.id, job.id);
      const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
      if (jobNumber) map.set(jobNumber, job.id);
    }
    return map;
  }, [jobs]);

  const [tab, setTab] = useState("shipments");
  const [search, setSearch] = useState("");
  const [shortageOnly, setShortageOnly] = useState(false);
  const [allocationFilter, setAllocationFilter] = useState<"required" | "available" | "missing" | "allocated">("required");
  const [allocationsStatusFilter, setAllocationsStatusFilter] = useState<WarehouseAllocationLane>("active");
  const [markingReadyIds, setMarkingReadyIds] = useState<Set<string>>(new Set());
  const [markingInstalledIds, setMarkingInstalledIds] = useState<Set<string>>(new Set());
  const [reAllocRow, setReAllocRow] = useState<JobAllocation | null>(null);
  const [selectedReceiving, setSelectedReceiving] = useState<ReceivingRecord | null>(null);
  const [showReceiving, setShowReceiving] = useState(false);
  const [selectedAllocate, setSelectedAllocate] = useState<ReceivingRecord | null>(null);
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocateContext, setAllocateContext] = useState<{ requiredQty: number; alreadyAllocated: number } | null>(null);
  const [partialAllocRow, setPartialAllocRow] = useState<JobMaterialAllocationRow | null>(null);
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [showShipmentReceiving, setShowShipmentReceiving] = useState(false);
  const [deliverDateDialog, setDeliverDateDialog] = useState<Shipment | null>(null);
  const [deliverDateValue, setDeliverDateValue] = useState<string>(() => toLocalDateInputValue(new Date().toISOString()));
  const [expandedShipment, setExpandedShipment] = useState<string | null>(null);
  const [expandedCompletedShipment, setExpandedCompletedShipment] = useState<string | null>(null);
  const [inventoryImportOpen, setInventoryImportOpen] = useState(false);
  const [inventoryImportMode, setInventoryImportMode] = useState<InventoryCsvImportMode>("add");
  const [inventoryImportPreview, setInventoryImportPreview] = useState<InventoryCsvPreview | null>(null);
  const [inventoryImportSource, setInventoryImportSource] = useState<{ fileName: string; text: string } | null>(null);
  const [inventoryManualSkuByRow, setInventoryManualSkuByRow] = useState<Record<number, string>>({});
  const [inventoryImportBusy, setInventoryImportBusy] = useState(false);
  const warehouseServerPaging = WAREHOUSE_SERVER_PAGING_ENABLED;
  const realtimeRefreshToken = useWarehouseRealtimeRefreshSignal(warehouseServerPaging);
  const [manualRefreshToken, setManualRefreshToken] = useState(0);
  const [allocationRefreshToken, setAllocationRefreshToken] = useState(0);
  const serverRefreshToken = realtimeRefreshToken + manualRefreshToken;
  const refreshWarehouseServerPages = useCallback(() => {
    setManualRefreshToken((current) => current + 1);
  }, []);

  const incomingShipmentsServerControls = useCountedServerPagination(10);
  const completedShipmentsServerControls = useCountedServerPagination(10);
  const receivingRecordsServerControls = useCountedServerPagination(20);
  const jobMaterialAllocationServerControls = useCountedServerPagination(15);
  const receivedNotAllocatedServerControls = useCountedServerPagination(10);
  const visibleAllocationsServerControls = useCountedServerPagination(20);
  const jobReadinessServerControls = useCountedServerPagination(15);
  const pendingSkuApprovalsServerControls = useCountedServerPagination(10);
  const discrepanciesServerControls = useCountedServerPagination(10);
  const stockServerControls = useCountedServerPagination(20);
  const lowStockServerControls = useCountedServerPagination(10);

  const warehouseSummary = useWarehouseSummary({
    enabled: warehouseServerPaging,
    refreshToken: serverRefreshToken,
  });
  const incomingShipmentsServer = useWarehouseShipmentsPage({
    enabled: warehouseServerPaging,
    bucket: "incoming",
    page: incomingShipmentsServerControls.page,
    pageSize: incomingShipmentsServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const completedShipmentsServer = useWarehouseShipmentsPage({
    enabled: warehouseServerPaging,
    bucket: "completed",
    page: completedShipmentsServerControls.page,
    pageSize: completedShipmentsServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const receivingRecordsServer = useWarehouseReceivingPage({
    enabled: warehouseServerPaging,
    lane: "all",
    page: receivingRecordsServerControls.page,
    pageSize: receivingRecordsServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const receivedNotAllocatedServer = useWarehouseReceivingPage({
    enabled: warehouseServerPaging,
    lane: "received_not_allocated",
    page: receivedNotAllocatedServerControls.page,
    pageSize: receivedNotAllocatedServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const discrepanciesServer = useWarehouseReceivingPage({
    enabled: warehouseServerPaging,
    lane: "issues",
    page: discrepanciesServerControls.page,
    pageSize: discrepanciesServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const visibleAllocationsServer = useWarehouseAllocationsPage({
    enabled: warehouseServerPaging,
    lane: allocationsStatusFilter,
    page: visibleAllocationsServerControls.page,
    pageSize: visibleAllocationsServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  useEffect(() => {
    jobMaterialAllocationServerControls.reset();
    setAllocationRefreshToken((t) => t + 1);
  }, [allocationFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    visibleAllocationsServerControls.reset();
  }, [allocationsStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const jobMaterialAllocationServer = useWarehouseJobMaterialAllocationPage({
    enabled: warehouseServerPaging,
    page: jobMaterialAllocationServerControls.page,
    pageSize: jobMaterialAllocationServerControls.pageSize,
    search,
    filter: allocationFilter,
    refreshToken: serverRefreshToken + allocationRefreshToken,
  });
  const jobReadinessServer = useWarehouseReadinessPage({
    enabled: warehouseServerPaging,
    lane: "all",
    page: jobReadinessServerControls.page,
    pageSize: jobReadinessServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const pendingSkuApprovalsServer = useWarehousePendingSkuApprovalsPage({
    enabled: warehouseServerPaging,
    page: pendingSkuApprovalsServerControls.page,
    pageSize: pendingSkuApprovalsServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const stockServer = useWarehouseStockPage({
    enabled: warehouseServerPaging,
    lane: "all",
    page: stockServerControls.page,
    pageSize: stockServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });
  const lowStockServer = useWarehouseStockPage({
    enabled: warehouseServerPaging,
    lane: "low",
    page: lowStockServerControls.page,
    pageSize: lowStockServerControls.pageSize,
    search,
    refreshToken: serverRefreshToken,
  });

  useEffect(() => { incomingShipmentsServerControls.setTotalItems(incomingShipmentsServer.count); }, [incomingShipmentsServer.count]);
  useEffect(() => { completedShipmentsServerControls.setTotalItems(completedShipmentsServer.count); }, [completedShipmentsServer.count]);
  useEffect(() => { receivingRecordsServerControls.setTotalItems(receivingRecordsServer.count); }, [receivingRecordsServer.count]);
  useEffect(() => { jobMaterialAllocationServerControls.setTotalItems(jobMaterialAllocationServer.count); }, [jobMaterialAllocationServer.count]);
  useEffect(() => { receivedNotAllocatedServerControls.setTotalItems(receivedNotAllocatedServer.count); }, [receivedNotAllocatedServer.count]);
  useEffect(() => { visibleAllocationsServerControls.setTotalItems(visibleAllocationsServer.count); }, [visibleAllocationsServer.count]);
  useEffect(() => { jobReadinessServerControls.setTotalItems(jobReadinessServer.count); }, [jobReadinessServer.count]);
  useEffect(() => { pendingSkuApprovalsServerControls.setTotalItems(pendingSkuApprovalsServer.count); }, [pendingSkuApprovalsServer.count]);
  useEffect(() => { discrepanciesServerControls.setTotalItems(discrepanciesServer.count); }, [discrepanciesServer.count]);
  useEffect(() => { stockServerControls.setTotalItems(stockServer.count); }, [stockServer.count]);

  useEffect(() => {
    if (stockSubTab === "all") return;
    locationStockPage.reset();
    if (warehouseStockCache[stockSubTab]) {
      setStockLocationLoading(false);
      return;
    }
    setStockLocationLoading(true);
    WarehouseTransferService.listStockByWarehouse(stockSubTab)
      .then((items) => {
        setWarehouseStockCache((prev) => ({ ...prev, [stockSubTab]: items }));
        setStockLocationLoading(false);
      })
      .catch(() => setStockLocationLoading(false));
  }, [stockSubTab]);
  useEffect(() => { lowStockServerControls.setTotalItems(lowStockServer.count); }, [lowStockServer.count]);

  const pendingReceiving = useMemo(() => receivingRecords.filter((r) => r.status !== "Fully Received"), [receivingRecords]);
  const receivedNotAllocated = useMemo(() => {
    const received = receivingRecords.filter((r) => r.status === "Fully Received");
    return received.filter((r) => {
      const allocations = jobAllocations.filter((a) => a.sku === r.sku && a.jobId === r.jobId);
      return allocations.length === 0;
    });
  }, [receivingRecords, jobAllocations]);
  const allocatedToJobs = useMemo(() => jobAllocations.filter((a) => a.status === "Reserved"), [jobAllocations]);
  const readyForInstall = useMemo(() => jobAllocations.filter((a) => a.status === "Ready for Install"), [jobAllocations]);
  const installedAllocations = useMemo(() => jobAllocations.filter((a) => a.status === "Installed"), [jobAllocations]);
  const discrepancies = useMemo(() => receivingRecords.filter((r) => r.discrepancy), [receivingRecords]);
  // Fetch all receiving records for the summary independently —
  // the heavy provider is deferred on this route so receivingRecords from
  // context is always empty here. We bypass that by fetching directly.
  const [allReceivingForSummary, setAllReceivingForSummary] = useState<ReceivingRecord[]>([]);
  useEffect(() => {
    void WarehouseService.fetchReceivingRecords().then(setAllReceivingForSummary);
  }, []);

  const receivingSummary = useMemo(() => {
    const bySkuMap = new Map<string, { sku: string; productName: string; totalReceived: number; status: string }>();
    for (const r of allReceivingForSummary) {
      const key = r.sku || r.productName;
      const existing = bySkuMap.get(key);
      if (existing) {
        existing.totalReceived += Math.max(0, r.receivedQuantity);
        // Upgrade status: Fully Received > Partially Received > Pending
        if (r.status === "Fully Received") existing.status = "Fully Received";
        else if (r.status === "Partially Received" && existing.status === "Pending") existing.status = "Partially Received";
      } else {
        bySkuMap.set(key, { sku: r.sku, productName: r.productName, totalReceived: Math.max(0, r.receivedQuantity), status: r.status });
      }
    }
    return Array.from(bySkuMap.values()).sort((a, b) => a.sku.localeCompare(b.sku));
  }, [allReceivingForSummary]);

   const [pendingSkuApprovals, setPendingSkuApprovals] = useState<PendingSkuApproval[]>([]);
   const [skuDraftByApprovalKey, setSkuDraftByApprovalKey] = useState<Record<string, string>>({});

   useEffect(() => {
     if (warehouseServerPaging) {
       setPendingSkuApprovals(pendingSkuApprovalsServer.approvals);
       return;
     }
     const fetchPendingSkuApprovals = async () => {
       const approvals: PendingSkuApproval[] = [];
       for (const job of jobs) {
         const unmapped = job.unmappedSkusForReview;
         if (unmapped && Array.isArray(unmapped)) {
           const jobLabel = typeof job.jobNumber === "string" ? job.jobNumber.trim() : job.id;
           for (const item of unmapped) {
             approvals.push({
               jobId: job.id,
               jobLabel,
               sku: item.sku,
               itemName: item.itemName,
               proposedSku: item.proposedSku,
               key: pendingSkuApprovalKey(job.id, item),
             });
           }
         }
       }
       setPendingSkuApprovals(approvals);
     };
     void fetchPendingSkuApprovals();
   }, [jobs, pendingSkuApprovalsServer.approvals, warehouseServerPaging]);

   useEffect(() => {
     setSkuDraftByApprovalKey((prev) => {
       const activeKeys = new Set(pendingSkuApprovals.map((approval) => approval.key));
       const next: Record<string, string> = {};
       for (const [key, value] of Object.entries(prev)) {
         if (activeKeys.has(key)) next[key] = value;
       }
       return next;
     });
   }, [pendingSkuApprovals]);

  const acceptSku = async (approval: PendingSkuApproval) => {
    try {
      const targetSku = normalizeWarehouseSku(skuDraftByApprovalKey[approval.key] ?? approval.proposedSku ?? approval.sku);
      if (!targetSku) {
        toast.error("Enter a SKU before approving.");
        return;
      }

      const job = jobs.find((candidate) => candidate.id === approval.jobId)
        ?? (await JobService.getById(approval.jobId)).data;
      if (!job) {
        toast.error("Could not find the job for this SKU review.");
        return;
      }

      const products = await getProducts();
      const existingProduct = products.find((product) => product.sku.toUpperCase() === targetSku);

      if (!existingProduct) {
        const product: RegistryProduct = {
          productId: crypto.randomUUID(),
          productName: approval.itemName,
          sku: targetSku,
          vendor: "Warehouse Review",
          category: "Other" as ProductCategory,
          subcategory: "Migration",
          unitOfMeasure: "each",
          cost: 0,
          price: 0,
          trackInventory: true,
          stockQuantity: 0,
          reorderThreshold: 1,
          active: true,
          notes: "Approved from migration unmapped SKU review.",
        };
        await createProduct(product);
      }

      if (targetSku !== approval.sku) {
        const { error: materialError } = await supabase
          .from("job_materials")
          .update({ sku: targetSku })
          .eq("job_id", approval.jobId)
          .eq("sku", approval.sku);
        if (materialError) throw materialError;
      }

      const nextMaterials = (job.materialsList ?? []).map((item) => {
        const itemSku = String(item.sku ?? "").trim();
        if (itemSku !== approval.sku && item.itemName !== approval.itemName) return item;
        return { ...item, sku: targetSku };
      });
      const remainingReviews = removePendingSkuReview(job.unmappedSkusForReview, job.id, approval.key);

      if (warehouseServerPaging) {
        const resolved = await WarehousePageService.resolvePendingSkuReview(approval.reviewId, "approved", targetSku);
        if (!resolved) throw new Error("Could not update the warehouse SKU review row.");

        const hadLegacyReview = (job.unmappedSkusForReview ?? []).length !== remainingReviews.length;
        if (hadLegacyReview) {
          const updateResult = await JobService.update(job.id, {
            unmappedSkusForReview: remainingReviews,
          });
          if (updateResult.error) {
            console.warn("[WarehouseInventoryPage] Legacy SKU review metadata cleanup failed:", updateResult.error);
          }
        }
      } else {
        const updateResult = await JobService.update(job.id, {
          materialsList: nextMaterials,
          unmappedSkusForReview: remainingReviews,
        });
        if (updateResult.error) throw updateResult.error;
      }

      setPendingSkuApprovals((current) => current.filter((item) => item.key !== approval.key));
      setSkuDraftByApprovalKey((current) => {
        const next = { ...current };
        delete next[approval.key];
        return next;
      });
      if (warehouseServerPaging) refreshWarehouseServerPages();
      else await refreshData();
      toast.success(
        existingProduct
          ? `Matched ${approval.sku} to existing SKU ${targetSku}`
          : `Approved and generated SKU ${targetSku}`,
      );
    } catch (err) {
      toast.error(`Failed to accept SKU: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const rejectSku = async (approval: PendingSkuApproval) => {
    try {
      const job = jobs.find((candidate) => candidate.id === approval.jobId)
        ?? (await JobService.getById(approval.jobId)).data;
      if (!job) {
        toast.error("Could not find the job for this SKU review.");
        return;
      }
      const remainingReviews = removePendingSkuReview(job.unmappedSkusForReview, job.id, approval.key);

      if (warehouseServerPaging) {
        const resolved = await WarehousePageService.resolvePendingSkuReview(approval.reviewId, "rejected");
        if (!resolved) throw new Error("Could not update the warehouse SKU review row.");

        const hadLegacyReview = (job.unmappedSkusForReview ?? []).length !== remainingReviews.length;
        if (hadLegacyReview) {
          const updateResult = await JobService.update(job.id, {
            unmappedSkusForReview: remainingReviews,
          });
          if (updateResult.error) {
            console.warn("[WarehouseInventoryPage] Legacy SKU review metadata cleanup failed:", updateResult.error);
          }
        }
      } else {
        const updateResult = await JobService.update(job.id, {
          unmappedSkusForReview: remainingReviews,
        });
        if (updateResult.error) throw updateResult.error;
      }

      setPendingSkuApprovals((current) => current.filter((item) => item.key !== approval.key));
      setSkuDraftByApprovalKey((current) => {
        const next = { ...current };
        delete next[approval.key];
        return next;
      });
      if (warehouseServerPaging) refreshWarehouseServerPages();
      else await refreshData();
      toast.message(`SKU ${approval.sku} marked as unmapped (not added to registry)`);
    } catch (err) {
      toast.error(`Failed to reject SKU: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  // Phase 4A.5 — per-job shortage map (read-only). Empty for jobs without bound spec.
  const shortageJobIds = useMemo(() => {
    const ids = new Set<string>();
    for (const job of jobs) {
      if (!job.boundJobSpec) continue;
      const rows = getAllocationCoverage(job, jobAllocations);
      if (rows.some((r) => r.isShort)) ids.add(job.id);
    }
    return ids;
  }, [jobs, jobAllocations]);
  const shortageCount = shortageJobIds.size;

  // Shipment groups
  const incomingShipments = useMemo(() => shipments.filter(s => s.status !== "Received"), [shipments]);
  const completedShipments = useMemo(() => shipments.filter(s => s.status === "Received"), [shipments]);

  // Job readiness summary — normalize IDs so migrated jobs stored by label
  // in one table and by UUID in another don't appear as duplicate rows.
  const jobIds = useMemo(() => {
    const ids = new Set<string>();
    const normalize = (id: string) => canonicalJobId.get(id) ?? id;
    receivingRecords.forEach(r => ids.add(normalize(r.jobId)));
    jobAllocations.forEach(a => ids.add(normalize(a.jobId)));
    return Array.from(ids);
  }, [receivingRecords, jobAllocations, canonicalJobId]);

  const jobReadiness = useMemo(() =>
    jobIds.map(id => {
      const rawLabel = receivingRecords.find(r => r.jobId === id)?.jobLabel || jobAllocations.find(a => a.jobId === id)?.jobLabel || id;
      const label = displayJobRef(id, rawLabel);
      return { jobId: id, jobLabel: label, ...isJobReadyForInstall(id) };
    }),
  [jobIds, isJobReadyForInstall, receivingRecords, jobAllocations, displayJobRef]);

  const filteredStock = useMemo(() => {
    if (!search) return stockRecords;
    const s = search.toLowerCase();
    return stockRecords.filter((r) => r.productName.toLowerCase().includes(s) || r.sku.toLowerCase().includes(s));
  }, [stockRecords, search]);

  const inventoryImportSummary = useMemo(() => {
    const rows = inventoryImportPreview?.rows ?? [];
    return {
      created: rows.filter((row) => row.action === "created").length,
      updated: rows.filter((row) => row.action === "updated").length,
      unchanged: rows.filter((row) => row.action === "unchanged").length,
      invalid: rows.filter((row) => row.action === "invalid").length,
      changed: rows.filter((row) => row.action === "created" || row.action === "updated").length,
    };
  }, [inventoryImportPreview]);

  const jobMaterialAllocationRows = useMemo<JobMaterialAllocationRow[]>(() => {
    return jobs.flatMap((job) => {
      const materials = job.materialsList ?? [];
      const jobLabel = job.customerName
        ? `${job.customerName} – ${getProductLabel(job)}`
        : job.jobNumber || job.id;
      return materials
        .map((material, index) => {
          const rawSku = String(material.sku || "").trim();
          const sku = normalizeWarehouseSku(rawSku);
          const skuKey = normalizeAllocationSku(sku);
          const requiredQuantity = materialRequiredQuantity(material);
          if (!sku || requiredQuantity <= 0) return null;

          const allocatedQuantity = jobAllocations
            .filter((allocation) => allocation.jobId === job.id
              && normalizeAllocationSku(allocation.sku) === skuKey
              && (allocation.status === "Reserved" || allocation.status === "Ready for Install" || allocation.status === "Installed"))
            .reduce((sum, allocation) => sum + Math.max(0, Number(allocation.quantity || 0)), 0);
          const stock = stockRecords.find((record) => normalizeAllocationSku(record.sku) === skuKey);
          const availableStock = Math.max(0, Number(stock?.availableStock || 0));
          const missingQuantity = Math.max(0, requiredQuantity - allocatedQuantity);
          const productName = material.itemName || stock?.productName || sku;

          return {
            key: `${job.id}:${sku}:${index}`,
            jobId: job.id,
            jobLabel,
            customerName: job.customerName || "",
            sku,
            productName,
            requiredQuantity,
            allocatedQuantity,
            missingQuantity,
            availableStock,
            recommendedQuantity: Math.min(missingQuantity, availableStock),
            hasStockMatch: Boolean(stock),
          };
        })
        .filter((row): row is JobMaterialAllocationRow => Boolean(row));
    });
  }, [jobs, jobAllocations, stockRecords]);

  const filteredJobMaterialAllocationRows = useMemo(() => {
    let base: JobMaterialAllocationRow[];
    switch (allocationFilter) {
      case "available":
        base = jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0 && row.hasStockMatch && row.availableStock > 0);
        break;
      case "missing":
        base = jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0 && (!row.hasStockMatch || row.availableStock <= 0));
        break;
      case "allocated":
        base = jobMaterialAllocationRows.filter((row) => row.requiredQuantity > 0 && row.allocatedQuantity >= row.requiredQuantity);
        break;
      default:
        base = jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0);
    }
    if (!search) return base;
    const q = search.toLowerCase();
    return base.filter((row) =>
      row.jobLabel.toLowerCase().includes(q)
      || row.customerName.toLowerCase().includes(q)
      || row.sku.toLowerCase().includes(q)
      || row.productName.toLowerCase().includes(q)
    );
  }, [jobMaterialAllocationRows, search, allocationFilter]);

  const visibleAllocations = useMemo(() => {
    let base = allocationsStatusFilter === "reserved"
      ? allocatedToJobs
      : allocationsStatusFilter === "ready"
        ? readyForInstall
        : allocationsStatusFilter === "installed"
          ? installedAllocations
          : [...allocatedToJobs, ...readyForInstall, ...installedAllocations];
    // Shortage filter only applies to active/reserved/ready lanes
    if (shortageOnly && allocationsStatusFilter !== "installed") {
      return base.filter((a) => shortageJobIds.has(a.jobId));
    }
    return base;
  }, [allocatedToJobs, readyForInstall, installedAllocations, shortageOnly, shortageJobIds, allocationsStatusFilter]);

  const allocationMatchSummary = useMemo(() => {
    const openRows = jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0);
    return {
      openRows: openRows.length,
      matchableRows: openRows.filter((row) => row.hasStockMatch && row.availableStock > 0).length,
      unmatchedRows: openRows.filter((row) => !row.hasStockMatch).length,
      noAvailableRows: openRows.filter((row) => row.hasStockMatch && row.availableStock <= 0).length,
    };
  }, [jobMaterialAllocationRows]);

  const recentInventoryImportAudits = useMemo(() => {
    return systemEvents
      .filter((event) => event.type === "admin_override"
        && event.payload?.domain === "warehouse_inventory_csv_import"
        && event.payload?.action === "inventory_csv_import_applied")
      .slice()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 5);
  }, [systemEvents]);

  const locationStockPage = usePagination(warehouseStockCache[stockSubTab] ?? [], 20);
  const incomingShipmentsClientPage = usePagination(incomingShipments, 10);
  const completedShipmentsClientPage = usePagination(completedShipments, 10);
  const receivingRecordsClientPage = usePagination(receivingRecords, 20);
  const receivingSummaryPage = usePagination(receivingSummary, 10);
  const jobMaterialAllocationClientPage = usePagination(filteredJobMaterialAllocationRows, 15);
  const receivedNotAllocatedClientPage = usePagination(receivedNotAllocated, 10);
  const visibleAllocationsClientPage = usePagination(visibleAllocations, 20);
  const jobReadinessClientPage = usePagination(jobReadiness, 15);
  const pendingSkuApprovalsClientPage = usePagination(pendingSkuApprovals, 10);
  const discrepanciesClientPage = usePagination(discrepancies, 10);
  const stockClientPage = usePagination(filteredStock, 20);
  const lowStockClientPage = usePagination(lowStockItems, 10);
  const inventoryImportPreviewRowsPage = usePagination(inventoryImportPreview?.rows ?? [], 20);

  const incomingShipmentsPage = warehouseServerPaging
    ? withServerRows(incomingShipmentsServerControls, incomingShipmentsServer.shipments)
    : incomingShipmentsClientPage;
  const completedShipmentsPage = warehouseServerPaging
    ? withServerRows(completedShipmentsServerControls, completedShipmentsServer.shipments)
    : completedShipmentsClientPage;
  const receivingRecordsPage = warehouseServerPaging
    ? withServerRows(receivingRecordsServerControls, receivingRecordsServer.records)
    : receivingRecordsClientPage;
  const jobMaterialAllocationPage = warehouseServerPaging
    ? withServerRows(jobMaterialAllocationServerControls, jobMaterialAllocationServer.rows)
    : jobMaterialAllocationClientPage;
  const receivedNotAllocatedPage = warehouseServerPaging
    ? withServerRows(receivedNotAllocatedServerControls, receivedNotAllocatedServer.records)
    : receivedNotAllocatedClientPage;
  const visibleAllocationsPage = warehouseServerPaging
    ? withServerRows(visibleAllocationsServerControls, visibleAllocationsServer.allocations)
    : visibleAllocationsClientPage;
  const jobReadinessPage = warehouseServerPaging
    ? withServerRows(jobReadinessServerControls, jobReadinessServer.rows)
    : jobReadinessClientPage;
  const pendingSkuApprovalsPage = warehouseServerPaging
    ? withServerRows(pendingSkuApprovalsServerControls, pendingSkuApprovalsServer.approvals)
    : pendingSkuApprovalsClientPage;
  const discrepanciesPage = warehouseServerPaging
    ? withServerRows(discrepanciesServerControls, discrepanciesServer.records)
    : discrepanciesClientPage;
  const stockPage = warehouseServerPaging
    ? withServerRows(stockServerControls, stockServer.stock)
    : stockClientPage;
  const lowStockPage = warehouseServerPaging
    ? withServerRows(lowStockServerControls, lowStockServer.stock)
    : lowStockClientPage;

  const warehouseKpis = warehouseSummary.data;

  const allocationFilterCounts = useMemo(() => {
    if (warehouseServerPaging && warehouseKpis) {
      return {
        required: warehouseKpis.allocationOpenRows,
        available: warehouseKpis.allocationMatchableRows,
        missing: warehouseKpis.allocationUnmatchedRows + warehouseKpis.allocationNoAvailableRows,
        allocated: warehouseKpis.allocationAllocatedRows,
      };
    }
    return {
      required: jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0).length,
      available: jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0 && row.hasStockMatch && row.availableStock > 0).length,
      missing: jobMaterialAllocationRows.filter((row) => row.missingQuantity > 0 && (!row.hasStockMatch || row.availableStock <= 0)).length,
      allocated: jobMaterialAllocationRows.filter((row) => row.requiredQuantity > 0 && row.allocatedQuantity >= row.requiredQuantity).length,
    };
  }, [warehouseServerPaging, warehouseKpis, jobMaterialAllocationRows]);

  const incomingShipmentsCount = warehouseServerPaging ? incomingShipmentsServer.count : incomingShipments.length;
  const completedShipmentsCount = warehouseServerPaging ? completedShipmentsServer.count : completedShipments.length;
  const pendingReceivingCount = warehouseServerPaging ? (warehouseKpis?.pendingReceiving ?? 0) : pendingReceiving.length;
  const receivedNotAllocatedCount = warehouseServerPaging ? receivedNotAllocatedServer.count : receivedNotAllocated.length;
  const allocatedCount = warehouseServerPaging ? (warehouseKpis?.allocated ?? 0) : allocatedToJobs.length;
  const readyForInstallCount = warehouseServerPaging ? (warehouseKpis?.readyForInstall ?? 0) : readyForInstall.length;
  // In server-paging mode the installed count is accurate only when that lane is active;
  // for other lanes we fall back to 0 (count is a display hint, not authoritative).
  const installedCount = warehouseServerPaging
    ? (allocationsStatusFilter === "installed" ? visibleAllocationsServer.count : 0)
    : installedAllocations.length;
  const discrepanciesCount = warehouseServerPaging ? discrepanciesServer.count : discrepancies.length;
  const jobMaterialAllocationOpenCount = warehouseServerPaging
    ? jobMaterialAllocationServer.count
    : filteredJobMaterialAllocationRows.length;
  const allocationMatchableCount = warehouseServerPaging ? (warehouseKpis?.allocationMatchableRows ?? 0) : allocationMatchSummary.matchableRows;
  const allocationUnmatchedCount = warehouseServerPaging ? (warehouseKpis?.allocationUnmatchedRows ?? 0) : allocationMatchSummary.unmatchedRows;
  const allocationNoAvailableCount = warehouseServerPaging ? (warehouseKpis?.allocationNoAvailableRows ?? 0) : allocationMatchSummary.noAvailableRows;
  const allocatedTotalCount = warehouseServerPaging
    ? visibleAllocationsServer.count
    : allocatedToJobs.length + readyForInstall.length + installedAllocations.length;
  const jobReadinessCount = warehouseServerPaging ? jobReadinessServer.count : jobReadiness.length;
  const pendingSkuApprovalsCount = warehouseServerPaging ? pendingSkuApprovalsServer.count : pendingSkuApprovals.length;
  const lowStockCount = warehouseServerPaging ? lowStockServer.count : lowStockItems.length;

  useEffect(() => {
    incomingShipmentsClientPage.reset();
    completedShipmentsClientPage.reset();
    receivingRecordsClientPage.reset();
    jobMaterialAllocationClientPage.reset();
    receivedNotAllocatedClientPage.reset();
    visibleAllocationsClientPage.reset();
    jobReadinessClientPage.reset();
    pendingSkuApprovalsClientPage.reset();
    discrepanciesClientPage.reset();
    stockClientPage.reset();
    lowStockClientPage.reset();
    incomingShipmentsServerControls.reset();
    completedShipmentsServerControls.reset();
    receivingRecordsServerControls.reset();
    jobMaterialAllocationServerControls.reset();
    receivedNotAllocatedServerControls.reset();
    visibleAllocationsServerControls.reset();
    jobReadinessServerControls.reset();
    pendingSkuApprovalsServerControls.reset();
    discrepanciesServerControls.reset();
    stockServerControls.reset();
    lowStockServerControls.reset();
    inventoryImportPreviewRowsPage.reset();
  }, [search, tab, shortageOnly, inventoryImportPreview?.fileName, inventoryImportMode]);

  const handleConfirmReceiving = useCallback(async (
    recordId: string,
    quantityReceived: number,
    discrepancy?: { type: DiscrepancyType; notes: string },
  ) => {
    if (!warehouseServerPaging) {
      confirmReceiving(recordId, quantityReceived, discrepancy);
      return;
    }
    const record = selectedReceiving ?? receivingRecordsPage.paged.find((item) => item.id === recordId);
    if (!record) {
      toast.error("Could not find this receiving row. Refresh and try again.");
      return;
    }
    const ok = await WarehousePageService.receiveRecord(record, quantityReceived, discrepancy);
    if (!ok) {
      toast.error("Receiving update failed.");
      return;
    }
    refreshWarehouseServerPages();
  }, [confirmReceiving, receivingRecordsPage.paged, refreshWarehouseServerPages, selectedReceiving, warehouseServerPaging]);

  const handleAllocateToJob = useCallback(async (
    sku: string,
    jobId: string,
    jobLabel: string,
    quantity: number,
    productName?: string,
  ) => {
    const allocation = await allocateToJob(sku, jobId, jobLabel, quantity, productName);
    if (allocation && warehouseServerPaging) refreshWarehouseServerPages();
    return allocation;
  }, [allocateToJob, refreshWarehouseServerPages, warehouseServerPaging]);

  const handleShipmentReceiving = useCallback(async (
    shipmentId: string,
    items: { sku: string; received: number; discrepancy?: { type: DiscrepancyType; notes: string } }[],
  ) => {
    if (!warehouseServerPaging) {
      receiveShipment(shipmentId, items);
      return;
    }
    const ok = await WarehousePageService.receiveShipmentItems(shipmentId, items);
    refreshWarehouseServerPages(); // always refresh — DB state is authoritative even when sync side effects fail
    if (!ok) {
      toast.error("Some items could not be fully processed. Check the receiving records.");
    }
  }, [receiveShipment, refreshWarehouseServerPages, warehouseServerPaging]);

  const handleShipmentStatus = useCallback(async (shipmentId: string, status: ShipmentStatus, deliveredAt?: string) => {
    if (!warehouseServerPaging) {
      updateShipmentStatus(shipmentId, status);
      return true;
    }
    const ok = await WarehouseService.updateShipmentStatus(shipmentId, status, deliveredAt);
    if (ok) refreshWarehouseServerPages();
    return ok;
  }, [refreshWarehouseServerPages, updateShipmentStatus, warehouseServerPaging]);

  const handleMarkReadyForInstall = useCallback(async (allocationId: string) => {
    if (!warehouseServerPaging) {
      markReadyForInstall(allocationId);
      return true;
    }
    const ok = await WarehouseService.updateJobAllocation(allocationId, {
      status: "Ready for Install",
      allocatedAt: new Date().toISOString(),
      allocatedBy: "Warehouse",
    });
    if (ok) refreshWarehouseServerPages();
    return ok;
  }, [markReadyForInstall, refreshWarehouseServerPages, warehouseServerPaging]);

  const handleMarkAsInstalled = useCallback(async (allocationId: string) => {
    if (!warehouseServerPaging) {
      await markAsInstalled(allocationId);
      return true;
    }
    const ok = await WarehouseService.updateJobAllocation(allocationId, {
      status: "Installed",
      allocatedAt: new Date().toISOString(),
      allocatedBy: "Warehouse",
    });
    if (ok) refreshWarehouseServerPages();
    return ok;
  }, [markAsInstalled, refreshWarehouseServerPages, warehouseServerPaging]);

  const handleReAllocate = useCallback(async (destJobId: string, destJobLabel: string, qty: number) => {
    if (!reAllocRow) return;
    const result = await WarehouseService.reAllocate(
      reAllocRow.id,
      destJobId,
      destJobLabel,
      qty,
      reAllocRow.sku,
      reAllocRow.productName,
      reAllocRow.quantity,
      reAllocRow.jobId,
      reAllocRow.jobLabel,
    );
    if (result.ok) {
      toast.success(`${qty} × ${reAllocRow.sku} moved to ${destJobLabel || destJobId}`);
      refreshWarehouseServerPages();
    } else {
      toast.error(result.error ?? "Re-allocation failed");
    }
  }, [reAllocRow, refreshWarehouseServerPages]);

  const allocateJobMaterial = async (row: JobMaterialAllocationRow) => {
    if (row.recommendedQuantity <= 0) {
      toast.message(
        row.hasStockMatch
          ? `${row.sku} has no available stock to allocate.`
          : `${row.sku} does not match current stock.`,
      );
      return;
    }

    try {
      const allocation = await handleAllocateToJob(
        row.sku,
        row.jobId,
        row.jobLabel,
        row.recommendedQuantity,
        row.productName,
      );
      if (!allocation) {
        toast.message(`${row.sku} was not allocated. It may already be fully allocated for this job.`);
        return;
      }
      if (warehouseServerPaging) refreshWarehouseServerPages();
      else await refreshData();
      toast.success(`${allocation.quantity} × ${row.sku} allocated to ${displayJobRef(row.jobId, row.jobLabel)}`);
    } catch (err) {
      toast.error(`Allocation failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const allocateJobMaterialQty = async (row: JobMaterialAllocationRow, qty: number) => {
    try {
      const allocation = await handleAllocateToJob(row.sku, row.jobId, row.jobLabel, qty, row.productName);
      if (!allocation) {
        toast.message(`${row.sku} was not allocated. It may already be fully allocated for this job.`);
        return;
      }
      if (warehouseServerPaging) refreshWarehouseServerPages();
      else await refreshData();
      toast.success(`${allocation.quantity} × ${row.sku} allocated to ${displayJobRef(row.jobId, row.jobLabel)}`);
    } catch (err) {
      toast.error(`Allocation failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleInventoryCsvSelected = async (file: File | null) => {
    if (!file) return;
    try {
      setInventoryImportBusy(true);
      const text = await file.text();
      const products = await getProducts();
      setInventoryImportSource({ fileName: file.name, text });
      setInventoryManualSkuByRow({});
      setInventoryImportPreview(buildInventoryCsvPreview(text, file.name, inventoryImportMode, products));
    } catch (err) {
      toast.error(`Could not read CSV: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInventoryImportBusy(false);
    }
  };

  const repriceInventoryImportPreview = async (mode: InventoryCsvImportMode) => {
    setInventoryImportMode(mode);
    setInventoryManualSkuByRow({});
    if (!inventoryImportSource) {
      setInventoryImportPreview(null);
      return;
    }
    try {
      setInventoryImportBusy(true);
      const products = await getProducts();
      setInventoryImportPreview(buildInventoryCsvPreview(
        inventoryImportSource.text,
        inventoryImportSource.fileName,
        mode,
        products,
      ));
    } catch (err) {
      toast.error(`Could not recalculate import preview: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInventoryImportBusy(false);
    }
  };

  const applyManualInventorySkuMatch = async (row: InventoryCsvPreviewRow) => {
    const targetSku = normalizeWarehouseSku(inventoryManualSkuByRow[row.rowNumber] ?? "");
    if (!targetSku) {
      toast.error("Enter a SKU to match this CSV row.");
      return;
    }
    if (!Number.isFinite(row.importedQuantity) || row.importedQuantity < 0) {
      toast.error("Fix the CSV quantity before matching this row.");
      return;
    }

    try {
      setInventoryImportBusy(true);
      const products = await getProducts();
      const existing = products.find((product) => normalizeWarehouseSku(product.sku) === targetSku);
      const previousQuantity = Number(existing?.stockQuantity || 0);
      const nextQuantity = inventoryImportMode === "add"
        ? previousQuantity + row.importedQuantity
        : row.importedQuantity;
      const nextRow: InventoryCsvPreviewRow = {
        ...row,
        sku: targetSku,
        productName: row.productName === "—" ? existing?.productName ?? targetSku : row.productName,
        vendor: row.vendor || existing?.vendor || "CSV Import",
        category: row.category || existing?.category || "Other",
        subcategory: row.subcategory || existing?.subcategory || "CSV Import",
        unitOfMeasure: row.unitOfMeasure || existing?.unitOfMeasure || "each",
        cost: row.cost || existing?.cost || 0,
        price: row.price || existing?.price || 0,
        reorderThreshold: row.reorderThreshold || existing?.reorderThreshold || 0,
        previousQuantity,
        nextQuantity,
        action: existing ? (nextQuantity === previousQuantity ? "unchanged" : "updated") : "created",
        error: undefined,
        matchType: "manual",
        matchNote: existing
          ? `Manually matched to existing SKU ${existing.sku}`
          : `Manual SKU ${targetSku} will create a new product`,
      };

      setInventoryImportPreview((current) =>
        current
          ? {
            ...current,
            rows: current.rows.map((candidate) =>
              candidate.rowNumber === row.rowNumber ? nextRow : candidate
            ),
          }
          : current
      );
      setInventoryManualSkuByRow((current) => {
        const next = { ...current };
        delete next[row.rowNumber];
        return next;
      });
    } catch (err) {
      toast.error(`Could not match SKU: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInventoryImportBusy(false);
    }
  };

  const applyInventoryCsvImport = async () => {
    if (!inventoryImportPreview || inventoryImportSummary.changed === 0) return;

    const changes = inventoryImportPreview.rows.filter((row) => row.action === "created" || row.action === "updated");
    const importId = crypto.randomUUID();
    try {
      setInventoryImportBusy(true);
      for (const row of changes) {
        if (row.action === "created") {
          await createProduct({
            productId: crypto.randomUUID(),
            productName: row.productName,
            sku: row.sku,
            vendor: row.vendor || "CSV Import",
            category: row.category,
            subcategory: row.subcategory || "CSV Import",
            unitOfMeasure: row.unitOfMeasure || "each",
            cost: row.cost,
            price: row.price,
            trackInventory: true,
            stockQuantity: row.nextQuantity,
            reorderThreshold: row.reorderThreshold,
            active: true,
            notes: row.notes || `Created by CSV inventory import ${importId}.`,
          });
        } else {
          await updateProductStockBySku(row.sku, row.nextQuantity);
        }
      }

      await logInventoryCsvImportAudit({
        importId,
        fileName: inventoryImportPreview.fileName,
        mode: inventoryImportMode,
        totalRows: inventoryImportPreview.totalRows,
        changedRows: changes.length,
        invalidRows: inventoryImportSummary.invalid,
        changes: changes.map((row) => ({
          importId,
          fileName: inventoryImportPreview.fileName,
          mode: inventoryImportMode,
          sku: row.sku,
          productName: row.productName,
          previousQuantity: row.previousQuantity,
          importedQuantity: row.importedQuantity,
          nextQuantity: row.nextQuantity,
          action: row.action,
          rowNumber: row.rowNumber,
        })),
      });

	      if (warehouseServerPaging) {
	        refreshWarehouseServerPages();
	      } else {
	        await refreshWarehouseData();
	        await refreshData();
	      }
	      setInventoryImportPreview(null);
      setInventoryImportSource(null);
      setInventoryImportOpen(false);
      toast.success(`Inventory import applied: ${changes.length} SKU${changes.length === 1 ? "" : "s"} changed`);
    } catch (err) {
      toast.error(`Inventory import failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInventoryImportBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-2"><Boxes className="h-5 w-5" /> Warehouse Inventory Control</h1>
          <p className="text-xs text-muted-foreground">Track, receive, verify, and allocate materials at the SKU level</p>
        </div>
        <div className="flex items-center gap-2">
          {shortageCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge
                  className="text-[10px] gap-1 bg-warning/15 text-warning border-warning/30 cursor-pointer"
                  onClick={() => { setTab("allocations"); setShortageOnly(true); }}
                >
                  <AlertTriangle className="h-3 w-3" />
                  {shortageCount} job{shortageCount === 1 ? "" : "s"} with allocation shortage
                </Badge>
              </TooltipTrigger>
              <TooltipContent>Jobs where allocated quantity is below the bound JobSpec BOM. Click to filter.</TooltipContent>
            </Tooltip>
          )}
          <Badge variant="outline" className="text-[10px] gap-1"><ShieldCheck className="h-3 w-3" /> Read-Only Ordering</Badge>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="cursor-pointer" onClick={() => setTab("shipments")}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{incomingShipmentsCount}</p>
            <p className="text-[10px] text-muted-foreground">Incoming Shipments</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer" onClick={() => setTab("receiving")}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold">{pendingReceivingCount}</p>
            <p className="text-[10px] text-muted-foreground">Pending Receiving</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer" onClick={() => setTab("allocations")}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-info">{receivedNotAllocatedCount}</p>
            <p className="text-[10px] text-muted-foreground">Unallocated</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer" onClick={() => setTab("allocations")}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-primary">{allocatedCount}</p>
            <p className="text-[10px] text-muted-foreground">Allocated</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer" onClick={() => setTab("readiness")}>
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-success">{readyForInstallCount}</p>
            <p className="text-[10px] text-muted-foreground">Ready for Install</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer" onClick={() => setTab("issues")}>
          <CardContent className="p-3 text-center">
            <p className={`text-2xl font-bold ${discrepanciesCount > 0 ? "text-destructive" : ""}`}>{discrepanciesCount}</p>
            <p className="text-[10px] text-muted-foreground">Issues</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="shipments" className="text-xs">Incoming Shipments</TabsTrigger>
            <TabsTrigger value="receiving" className="text-xs">Receiving</TabsTrigger>
            <TabsTrigger value="allocations" className="text-xs">Allocations</TabsTrigger>
            <TabsTrigger value="readiness" className="text-xs">Install Readiness</TabsTrigger>
            <TabsTrigger value="issues" className="text-xs">Issues</TabsTrigger>
            <TabsTrigger value="stock" className="text-xs">Stock</TabsTrigger>
            <TabsTrigger value="hub" className="text-xs">Hub</TabsTrigger>
            <TabsTrigger value="transfers" className="text-xs">Transfers</TabsTrigger>
            <TabsTrigger value="audit" className="text-xs">Numbers Audit</TabsTrigger>
          </TabsList>
          <div className="relative w-56">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input className="pl-8 h-8 text-xs" placeholder="Search SKU or product..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {/* ── Incoming Shipments Tab ── */}
        <TabsContent value="shipments" className="space-y-3">
          {incomingShipmentsServer.loading && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Refreshing…
            </div>
          )}
          {incomingShipmentsCount === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">
              <Truck className="h-10 w-10 mx-auto mb-2 opacity-30" />
              No incoming shipments
            </CardContent></Card>
          ) : (
            incomingShipmentsPage.paged.map((shipment) => {
              const isExpanded = expandedShipment === shipment.id;
              const totalItems = shipment.items.reduce((s, i) => s + i.expectedQuantity, 0);
              const totalReceived = shipment.items.reduce((s, i) => s + i.receivedQuantity, 0);
              const nextStatus = NEXT_STATUS[shipment.status];
              const isStandalone = shipment.items.every((i) => !i.jobId);
              return (
                <Card key={shipment.id} className="overflow-hidden">
                  <Collapsible open={isExpanded} onOpenChange={() => setExpandedShipment(isExpanded ? null : shipment.id)}>
                    <CollapsibleTrigger asChild>
                      <div className="flex items-center gap-4 p-4 cursor-pointer hover:bg-muted/30 transition-colors">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isStandalone && (
                              <Badge className="text-[9px] bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30">
                                Direct Order
                              </Badge>
                            )}
                            <span className="font-semibold text-sm">{shipment.vendorName}</span>
                          </div>
                          <div className="flex flex-col gap-0.5 mt-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[9px] text-muted-foreground w-[58px] shrink-0">Internal PO</span>
                              <span className="text-[10px] font-mono text-blue-700 dark:text-blue-400">{formatPoNumber(shipment.poNumber)}</span>
                            </div>
                            {shipment.externalPoNumber && (
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] text-muted-foreground w-[58px] shrink-0">Vendor PO</span>
                                <span className="text-[10px] font-mono text-amber-700 dark:text-amber-400">{shipment.externalPoNumber}</span>
                              </div>
                            )}
                            {shipment.warehouseName && (
                              <Badge variant="outline" className="text-[9px] gap-1 px-1.5 font-normal w-fit">
                                <span className="text-muted-foreground">→</span>
                                {shipment.warehouseName}
                              </Badge>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">
                            {shipment.items.length} SKU(s) · {totalReceived}/{totalItems} received
                            {shipment.expectedDelivery && ` · ETA: ${formatShortDate(shipment.expectedDelivery)}`}
                            {!isStandalone && shipment.items[0] && displayJobRef(shipment.items[0].jobId!, shipment.items[0].jobLabel ?? undefined)
                              ? ` · ${displayJobRef(shipment.items[0].jobId!, shipment.items[0].jobLabel ?? undefined)}`
                              : null}
                          </p>
                        </div>
                        <Badge className={`text-[10px] ${SHIPMENT_STATUS_STYLE[shipment.status]}`}>{shipment.status}</Badge>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t bg-muted/10 p-4">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="text-[10px] h-7">SKU</TableHead>
                              <TableHead className="text-[10px] h-7">Product</TableHead>
                              {!isStandalone && <TableHead className="text-[10px] h-7">Job</TableHead>}
                              <TableHead className="text-[10px] h-7 text-right">Expected</TableHead>
                              <TableHead className="text-[10px] h-7 text-right">Received</TableHead>
                              <TableHead className="text-[10px] h-7">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {shipment.items.map((item, idx) => (
                              <TableRow key={idx}>
                                <TableCell className="text-[10px] font-mono py-1.5">{item.sku}</TableCell>
                                <TableCell className="text-xs py-1.5">{item.productName}</TableCell>
                                {!isStandalone && (
                                  <TableCell className="text-[10px] py-1.5 text-muted-foreground">
                                    {displayJobRef(item.jobId!, item.jobLabel ?? undefined)}
                                  </TableCell>
                                )}
                                <TableCell className="text-xs py-1.5 text-right">{item.expectedQuantity}</TableCell>
                                <TableCell className="text-xs py-1.5 text-right font-bold">{item.receivedQuantity}</TableCell>
                                <TableCell className="py-1.5">
                                  <Badge variant={item.receivedQuantity >= item.expectedQuantity ? "default" : "secondary"} className="text-[9px]">
                                    {item.receivedQuantity >= item.expectedQuantity ? "Received" : item.receivedQuantity > 0 ? "Partial" : "Pending"}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <div className="flex items-center gap-2 mt-3 pt-2 border-t">
                          {nextStatus && (
                            <PermissionGate permission="warehouse_edit">
                              <Button size="sm" variant="outline" className="text-xs h-7" onClick={async () => {
                                if (nextStatus === "Delivered") {
                                  setDeliverDateValue(toLocalDateInputValue(new Date().toISOString()));
                                  setDeliverDateDialog(shipment);
                                } else {
                                  const ok = await handleShipmentStatus(shipment.id, nextStatus);
                                  if (ok) toast.success(`Shipment marked as ${nextStatus}`);
                                  else toast.error(`Could not mark shipment as ${nextStatus}`);
                                }
                              }}>
                                Mark as {nextStatus}
                              </Button>
                            </PermissionGate>
                          )}
                          {shipment.status === "Delivered" && (
                            <Button size="sm" className="text-xs h-7" onClick={() => {
                              setSelectedShipment(shipment);
                              setShowShipmentReceiving(true);
                            }} {...actionPerms.guardProps("receive_inventory")}>
                              <PackageOpen className="h-3 w-3 mr-1" /> Receive Shipment
                            </Button>
                          )}
                          {/* Movement stage — auto-set by workflow (In Transit → On Site) */}
                          {shipment.movementStage && (
                            <div className="ml-auto flex items-center gap-1">
                              <span className="text-[10px] text-muted-foreground">Stage:</span>
                              <Badge variant="outline" className="text-[10px] px-1.5">
                                {shipment.movementStage}
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              );
            })
          )}
          <PaginationBar {...incomingShipmentsPage} />

          {completedShipmentsCount > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-bold mb-2 text-muted-foreground">Completed Shipments ({completedShipmentsCount})</h3>
              {completedShipmentsPage.paged.map(s => {
                const isStandalone = s.items.every((i) => !i.jobId);
                const jobRef = !isStandalone && s.items[0]
                  ? displayJobRef(s.items[0].jobId!, s.items[0].jobLabel ?? undefined)
                  : null;
                const isExpanded = expandedCompletedShipment === s.id;
                const allFullyReceived = s.items.every(i => i.receivedQuantity >= i.expectedQuantity);
                return (
                  <Card key={s.id} className="shadow-none mb-2 overflow-hidden">
                    <Collapsible open={isExpanded} onOpenChange={() => setExpandedCompletedShipment(isExpanded ? null : s.id)}>
                      <CollapsibleTrigger asChild>
                        <div className="p-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30 transition-colors">
                          {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <span className="font-semibold text-sm">{s.vendorName}</span>
                            <div className="flex flex-col gap-0.5 mt-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[9px] text-muted-foreground w-[58px] shrink-0">Internal PO</span>
                                <span className="text-[10px] font-mono text-blue-700 dark:text-blue-400">{formatPoNumber(s.poNumber)}</span>
                              </div>
                              {s.externalPoNumber && (
                                <div className="flex items-center gap-2">
                                  <span className="text-[9px] text-muted-foreground w-[58px] shrink-0">Vendor PO</span>
                                  <span className="text-[10px] font-mono text-amber-700 dark:text-amber-400">{s.externalPoNumber}</span>
                                </div>
                              )}
                              {s.warehouseName && (
                                <Badge variant="outline" className="text-[9px] gap-1 px-1.5 font-normal w-fit">
                                  <span className="text-muted-foreground">→</span>
                                  {s.warehouseName}
                                </Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {s.items.length} SKU(s) · Received {formatShortDate(s.receivedAt)}
                              {jobRef ? ` · ${jobRef}` : null}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {!allFullyReceived && (
                              <Badge variant="outline" className="text-[9px] gap-1 bg-warning/10 text-warning border-warning/30">
                                <AlertTriangle className="h-2.5 w-2.5" /> Partial
                              </Badge>
                            )}
                            <Badge className={`text-[10px] ${SHIPMENT_STATUS_STYLE["Received"]}`}>Received</Badge>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t bg-muted/10 p-3">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-[10px] h-7">SKU</TableHead>
                                <TableHead className="text-[10px] h-7">Product</TableHead>
                                {!isStandalone && <TableHead className="text-[10px] h-7">Job</TableHead>}
                                <TableHead className="text-[10px] h-7 text-right">Expected</TableHead>
                                <TableHead className="text-[10px] h-7 text-right">Received</TableHead>
                                <TableHead className="text-[10px] h-7">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {s.items.map((item, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="text-[10px] font-mono py-1.5">{item.sku}</TableCell>
                                  <TableCell className="text-xs py-1.5">{item.productName}</TableCell>
                                  {!isStandalone && (
                                    <TableCell className="text-[10px] py-1.5 text-muted-foreground">
                                      {displayJobRef(item.jobId!, item.jobLabel ?? undefined)}
                                    </TableCell>
                                  )}
                                  <TableCell className="text-xs py-1.5 text-right">{item.expectedQuantity}</TableCell>
                                  <TableCell className="text-xs py-1.5 text-right font-bold">{item.receivedQuantity}</TableCell>
                                  <TableCell className="py-1.5">
                                    <Badge variant={item.receivedQuantity >= item.expectedQuantity ? "default" : "secondary"} className="text-[9px]">
                                      {item.receivedQuantity >= item.expectedQuantity ? "Received" : item.receivedQuantity > 0 ? "Partial" : "Pending"}
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                );
              })}
              <PaginationBar {...completedShipmentsPage} />
            </div>
          )}
        </TabsContent>

        {/* ── Receiving Tab ── */}
        <TabsContent value="receiving">
          <Card>
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">PO #</TableHead>
                    <TableHead className="text-[10px]">Vendor</TableHead>
                    <TableHead className="text-[10px]">Job</TableHead>
                    <TableHead className="text-[10px]">SKU</TableHead>
                    <TableHead className="text-[10px]">SKU Status</TableHead>
                    <TableHead className="text-[10px]">Product</TableHead>
                    <TableHead className="text-[10px] text-right">Expected</TableHead>
                    <TableHead className="text-[10px] text-right">Received</TableHead>
                    <TableHead className="text-[10px]">Status</TableHead>
                    <TableHead className="text-[10px]">Received At</TableHead>
                    <TableHead className="text-[10px]">Received By</TableHead>
                    <TableHead className="text-[10px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receivingRecordsPage.paged.map((r) => {
                    const skuStatus = resolveWarehouseSkuStatus(r.sku, registrySkuSet);
                    const showUnmappedWarning = skuStatus === "unmapped" && Boolean(r.unmappedWarning);
                    return (
                    <TableRow key={r.id} className={r.discrepancy ? "bg-destructive/5" : ""}>
                      <TableCell className="text-xs font-mono">{formatPoNumber(r.poNumber)}</TableCell>
                      <TableCell className="text-xs">{r.vendorName}</TableCell>
                      <TableCell className="text-xs">{displayJobRef(r.jobId, r.jobLabel)}</TableCell>
                      <TableCell className="text-[10px] font-mono">{r.sku}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <SkuStatusBadge status={skuStatus} />
                          {showUnmappedWarning && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="destructive" className="text-[9px] gap-1">
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  Warning
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>SKU not recognized in canonical catalog. {r.unmappedWarning}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{r.productName}</TableCell>
                      <TableCell className="text-xs text-right">{r.expectedQuantity}</TableCell>
                      <TableCell className="text-xs text-right font-bold">{r.receivedQuantity}</TableCell>
                      <TableCell>
                        <Badge variant={r.status === "Fully Received" ? "default" : r.status === "Partially Received" ? "outline" : "secondary"} className="text-[9px]">
                          {r.status}
                        </Badge>
                        {r.discrepancy && <Badge variant="destructive" className="text-[9px] ml-1">{r.discrepancy.type}</Badge>}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground whitespace-nowrap">
                        {r.receivedAt ? formatStandardTimestamp(r.receivedAt) : "—"}
                      </TableCell>
                      <TableCell className="text-[10px] text-muted-foreground">
                        {r.receivedBy || "—"}
                      </TableCell>
                      <TableCell>
                        {r.status !== "Fully Received" && (
                          <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => { setSelectedReceiving(r); setShowReceiving(true); }}>
                            Receive
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <PaginationBar {...receivingRecordsPage} />
          </Card>

          {/* ── Receiving Summary ── */}
          {receivingSummary.length > 0 && (
            <Card className="mt-4">
              <div className="px-4 pt-3 pb-2">
                <p className="text-xs font-semibold text-foreground">Received Summary</p>
                <p className="text-[10px] text-muted-foreground">Total received per product across all records</p>
              </div>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">SKU</TableHead>
                      <TableHead className="text-[10px]">Product</TableHead>
                      <TableHead className="text-[10px]">Status</TableHead>
                      <TableHead className="text-[10px] text-right">Total Received</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receivingSummaryPage.paged.map((row) => (
                      <TableRow key={row.sku}>
                        <TableCell className="text-[10px] font-mono">{row.sku}</TableCell>
                        <TableCell className="text-xs">{row.productName}</TableCell>
                        <TableCell>
                          <Badge variant={row.status === "Fully Received" ? "default" : row.status === "Partially Received" ? "outline" : "secondary"} className="text-[9px]">
                            {row.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-right font-bold tabular-nums">{row.totalReceived}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <PaginationBar {...receivingSummaryPage} />
            </Card>
          )}
        </TabsContent>

        {/* ── Allocations Tab ── */}
        <TabsContent value="allocations" className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <h3 className="text-xs font-bold flex items-center gap-1">
                  <Boxes className="h-3.5 w-3.5" /> Allocate from Job Materials ({jobMaterialAllocationOpenCount})
                </h3>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Matches each job material SKU to warehouse stock and allocates the missing quantity.
                </p>
              </div>
              <div className="flex flex-wrap gap-1">
                <Badge className="text-[9px] bg-success/15 text-success border-success/30">
                  {allocationMatchableCount} ready to allocate
                </Badge>
                {allocationNoAvailableCount > 0 && (
                  <Badge className="text-[9px] bg-warning/15 text-warning border-warning/30">
                    {allocationNoAvailableCount} no stock
                  </Badge>
                )}
                {allocationUnmatchedCount > 0 && (
                  <Badge variant="destructive" className="text-[9px]">
                    {allocationUnmatchedCount} unmatched SKU
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-1.5 mb-2">
              {(["required", "available", "missing", "allocated"] as const).map((f) => {
                const labels: Record<typeof f, string> = { required: "Required", available: "Available", missing: "Missing", allocated: "Allocated" };
                const isActive = allocationFilter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setAllocationFilter(f)}
                    className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"}`}
                  >
                    {labels[f]} · {allocationFilterCounts[f]}
                  </button>
                );
              })}
            </div>
            <Card>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">Homeowner</TableHead>
                      <TableHead className="text-[10px]">Project Type</TableHead>
                      <TableHead className="text-[10px]">Material</TableHead>
                      <TableHead className="text-[10px]">SKU</TableHead>
                      <TableHead className="text-[10px] text-right">Required</TableHead>
                      <TableHead className="text-[10px] text-right">Allocated</TableHead>
                      <TableHead className="text-[10px] text-right">Missing</TableHead>
                      <TableHead className="text-[10px] text-right">Available</TableHead>
                      <TableHead className="text-[10px]">Match</TableHead>
                      <TableHead className="text-[10px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobMaterialAllocationPage.paged.map((row) => {
                      const canAllocate = row.recommendedQuantity > 0;
                      return (
                        <TableRow key={row.key} className={!row.hasStockMatch ? "bg-destructive/5" : row.availableStock <= 0 ? "bg-warning/5" : ""}>
                          <TableCell className="text-xs">
                            {row.customerName ? (
                              <span className="font-medium">{row.customerName}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.projectType ? (
                              <span className="font-medium">{row.projectType}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{row.productName}</TableCell>
                          <TableCell className="text-[10px] font-mono">{row.sku}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{row.requiredQuantity}</TableCell>
                          <TableCell className="text-xs text-right font-mono">
                            <div className="flex items-center justify-end gap-1">
                              {row.allocatedQuantity > 0 && row.missingQuantity > 0 && (
                                <span className="text-warning text-[10px]">●</span>
                              )}
                              <span>{row.allocatedQuantity}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono font-bold">{row.missingQuantity}</TableCell>
                          <TableCell className={`text-xs text-right font-mono ${row.availableStock <= 0 ? "text-warning" : "text-success"}`}>
                            {row.availableStock}
                          </TableCell>
                          <TableCell>
                            {!row.hasStockMatch ? (
                              <Badge variant="destructive" className="text-[9px]">No stock SKU match</Badge>
                            ) : row.availableStock <= 0 ? (
                              <Badge className="text-[9px] bg-warning/15 text-warning border-warning/30">Out of stock</Badge>
                            ) : (
                              <Badge className="text-[9px] bg-success/15 text-success border-success/30">Matched</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px]"
                              {...actionPerms.guardProps("receive_inventory")}
                              disabled={!canAllocate}
                              onClick={() => setPartialAllocRow(row)}
                            >
                              <ArrowRight className="h-3 w-3 mr-1" />
                              Allocate {row.recommendedQuantity > 0 ? row.recommendedQuantity : ""}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {jobMaterialAllocationOpenCount === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-xs text-muted-foreground py-8">
                          {allocationFilter === "allocated"
                            ? "No fully allocated materials found."
                            : allocationFilter === "available"
                              ? "No materials with available stock."
                              : allocationFilter === "missing"
                                ? "No materials with missing stock."
                                : "No job materials need allocation."}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <PaginationBar {...jobMaterialAllocationPage} />
            </Card>
          </div>

          {/* Unallocated */}
          {receivedNotAllocatedCount > 0 && (
            <div>
              <h3 className="text-xs font-bold mb-2 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Received — Needs Allocation ({receivedNotAllocatedCount})</h3>
              <div className="space-y-2">
                {receivedNotAllocatedPage.paged.map((r) => (
                  <Card key={r.id} className="shadow-none">
                    <CardContent className="p-3 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium">{r.productName}</p>
                        <p className="text-[10px] text-muted-foreground">{displayJobRef(r.jobId, r.jobLabel)}</p>
                        <Badge variant="outline" className="text-[9px] font-mono mt-1">{r.sku} × {r.receivedQuantity}</Badge>
                      </div>
                      <PermissionGate permission="warehouse_edit">
                        <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                          const match = jobMaterialAllocationRows.find(
                            (m) => m.jobId === r.jobId && normalizeAllocationSku(m.sku) === normalizeAllocationSku(r.sku)
                          );
                          setAllocateContext(match ? { requiredQty: match.requiredQuantity, alreadyAllocated: match.allocatedQuantity } : null);
                          setSelectedAllocate(r);
                          setShowAllocate(true);
                        }}>
                          <ArrowRight className="h-3 w-3 mr-1" /> Allocate
                        </Button>
                      </PermissionGate>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <PaginationBar {...receivedNotAllocatedPage} />
            </div>
          )}

          {/* Allocated */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h3 className="text-xs font-bold flex items-center gap-1"><ClipboardCheck className="h-3.5 w-3.5" /> Allocated to Jobs ({allocatedTotalCount})</h3>
              <Button
                size="sm"
                variant={shortageOnly ? "default" : "outline"}
                className="h-7 text-[10px] gap-1"
                onClick={() => setShortageOnly((v) => !v)}
                disabled={warehouseServerPaging || (shortageCount === 0 && !shortageOnly)}
              >
                <AlertTriangle className="h-3 w-3" />
                Shortage only ({shortageCount})
              </Button>
            </div>
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {(["active", "reserved", "ready", "installed"] as const).map((f) => {
                const labels: Record<typeof f, string> = { active: "All", reserved: "Reserved", ready: "Ready for Install", installed: "Installed" };
                const counts: Record<typeof f, number> = {
                  active: allocatedCount + readyForInstallCount + installedCount,
                  reserved: allocatedCount,
                  ready: readyForInstallCount,
                  installed: installedCount,
                };
                const isActive = allocationsStatusFilter === f;
                return (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setAllocationsStatusFilter(f)}
                    className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${isActive ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"}`}
                  >
                    {labels[f]} · {counts[f]}
                  </button>
                );
              })}
            </div>
            <Card>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">Homeowner</TableHead>
                      <TableHead className="text-[10px]">Project Type</TableHead>
                      <TableHead className="text-[10px]">SKU</TableHead>
                      <TableHead className="text-[10px]">Product</TableHead>
                      <TableHead className="text-[10px] text-right">Qty</TableHead>
                      <TableHead className="text-[10px]">Status</TableHead>
                      <TableHead className="text-[10px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleAllocationsPage.paged.map((a) => (
                      <TableRow key={a.id} className={a.overAvailableWarning ? "bg-destructive/5" : ""}>
                        <TableCell className="text-xs">
                          {a.customerName ? (
                            <span className="font-medium">{a.customerName}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {a.projectType ? (
                            <span className="font-medium">{a.projectType}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[10px] font-mono">{a.sku}</TableCell>
                        <TableCell className="text-xs">{a.productName}</TableCell>
                        <TableCell className="text-xs text-right">{a.quantity}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 flex-wrap">
                            <Badge variant={a.status === "Ready for Install" ? "default" : "outline"} className="text-[9px]">
                              {a.status}
                            </Badge>
                            {a.allocatedBy === "Field Purchase" && (
                              <Badge variant="outline" className="text-[9px] gap-1 bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-800/40">
                                <ShoppingBag className="h-2.5 w-2.5" /> Field Purchase
                              </Badge>
                            )}
                            {a.allocatedBy === "reallocation-override" && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="outline" className="text-[9px] gap-1 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800/40">
                                    <ArrowLeftRight className="h-2.5 w-2.5" /> Re-Allocated
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>Materials moved from another job via re-allocation.</TooltipContent>
                              </Tooltip>
                            )}
                            {a.overAvailableWarning && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="destructive" className="text-[9px] gap-1">
                                    <AlertTriangle className="h-2.5 w-2.5" /> Over-reserved
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>{a.overAvailableWarning}</TooltipContent>
                              </Tooltip>
                            )}
                            {shortageJobIds.has(a.jobId) && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="text-[9px] gap-1 bg-warning/15 text-warning border-warning/30">
                                    <AlertTriangle className="h-2.5 w-2.5" /> Job short
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>This job's allocations are below its bound JobSpec BOM.</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {a.status === "Reserved" && a.allocatedBy !== "Field Purchase" && (
                              <PermissionGate permission="warehouse_edit">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-[10px]"
                                  disabled={markingReadyIds.has(a.id)}
                                  onClick={async () => {
                                    setMarkingReadyIds((prev) => new Set(prev).add(a.id));
                                    const ok = await handleMarkReadyForInstall(a.id);
                                    setMarkingReadyIds((prev) => { const next = new Set(prev); next.delete(a.id); return next; });
                                    if (ok) toast.success("Marked ready for install");
                                    else toast.error("Could not mark allocation ready");
                                  }}
                                >
                                  {markingReadyIds.has(a.id) ? (
                                    <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Marking…</>
                                  ) : "Mark Ready"}
                                </Button>
                              </PermissionGate>
                            )}
                            {(a.status === "Reserved" || a.status === "Ready for Install") && (
                              <PermissionGate permission="warehouse_edit">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-[10px]"
                                  onClick={() => setReAllocRow(a)}
                                >
                                  <ArrowLeftRight className="h-3 w-3 mr-1" />
                                  Re-Allocate
                                </Button>
                              </PermissionGate>
                            )}
                            {a.status === "Ready for Install" && (
                              <PermissionGate permission="warehouse_edit">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 text-[10px] text-success hover:text-success"
                                  disabled={markingInstalledIds.has(a.id)}
                                  onClick={async () => {
                                    setMarkingInstalledIds((prev) => new Set(prev).add(a.id));
                                    const ok = await handleMarkAsInstalled(a.id);
                                    setMarkingInstalledIds((prev) => { const next = new Set(prev); next.delete(a.id); return next; });
                                    if (ok) toast.success("Marked as installed");
                                    else toast.error("Could not mark as installed");
                                  }}
                                >
                                  {markingInstalledIds.has(a.id) ? (
                                    <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Marking…</>
                                  ) : "Mark Installed"}
                                </Button>
                              </PermissionGate>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {allocatedTotalCount === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-8">No allocations</TableCell></TableRow>
                    )}
                    {shortageOnly && visibleAllocationsPage.totalItems === 0 && allocatedTotalCount > 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-8">No allocation shortages — all jobs meet their BOM.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <PaginationBar {...visibleAllocationsPage} />
            </Card>
          </div>
        </TabsContent>

        {/* ── Install Readiness Tab ── */}
        <TabsContent value="readiness" className="space-y-3">
          <p className="text-xs text-muted-foreground">Jobs are only "Ready for Install" when ALL materials are received, allocated, and marked ready.</p>
          {jobReadinessCount === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">No jobs tracked</CardContent></Card>
          ) : (
            jobReadinessPage.paged.map((jr) => (
              <Card key={jr.jobId} className={`border-l-4 ${jr.ready ? "border-l-emerald-500" : "border-l-amber-500"}`}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {jr.ready
                        ? <CheckCircle2 className="h-4 w-4 text-success" />
                        : <AlertTriangle className="h-4 w-4 text-warning" />
                      }
                      <span className="text-sm font-medium">
                        {(jr as any).customerName
                          ? `${(jr as any).customerName}${(jr as any).projectType ? ` — ${(jr as any).projectType}` : ""}`
                          : displayJobRef(jr.jobId, jr.jobLabel)
                        }
                      </span>
                      <Badge variant="outline" className="text-[10px]">{displayJobRef(jr.jobId, jr.jobLabel)}</Badge>
                    </div>
                    {!jr.ready && (
                      <div className="mt-1 space-y-0.5">
                        {jr.reasons.map((r, i) => (
                          <p key={i} className="text-[10px] text-warning flex items-center gap-1">
                            <XCircle className="h-3 w-3" /> {r}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <Badge className={`text-[10px] ${jr.ready ? "bg-success/15 text-success dark:bg-success/30 dark:text-success" : "bg-warning/15 text-warning dark:bg-warning/30 dark:text-warning"}`}>
                    {jr.ready ? "Ready for Install" : "Not Ready"}
                  </Badge>
                </CardContent>
              </Card>
            ))
          )}
          <PaginationBar {...jobReadinessPage} />
        </TabsContent>

        {/* ── Issues Tab ── */}
        <TabsContent value="issues" className="space-y-4">
          {/* Unmapped SKUs Pending Review */}
          {pendingSkuApprovalsCount > 0 && (
            <div>
              <h3 className="text-xs font-bold mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Unmapped SKUs Pending Warehouse Review ({pendingSkuApprovalsCount})
              </h3>
              <div className="space-y-2">
                {pendingSkuApprovalsPage.paged.map((approval) => (
                  <Card key={approval.key} className="border-l-4 border-l-warning">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          <span className="text-sm font-medium">{approval.itemName}</span>
                        </div>
                        <Badge variant="outline" className="text-[10px]">Pending Review</Badge>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        <span className="font-mono">{approval.sku}</span>
                        {approval.proposedSku && approval.proposedSku !== approval.sku && (
                          <> → <span className="font-mono text-primary">{approval.proposedSku}</span></>
                        )}
                        {" · Job: " + displayJobRef(approval.jobId, approval.jobLabel)}
                      </p>
                      <div className="mt-2 grid gap-2 md:grid-cols-[minmax(180px,1fr)_auto_auto]">
                        <Input
                          value={skuDraftByApprovalKey[approval.key] ?? approval.proposedSku}
                          onChange={(event) => setSkuDraftByApprovalKey((current) => ({
                            ...current,
                            [approval.key]: event.target.value,
                          }))}
                          className="h-7 font-mono text-xs"
                          placeholder="Existing or new SKU"
                        />
                        <PermissionGate permission="warehouse_edit">
                          <Button
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => void acceptSku(approval)}
                          >
                            Approve / Match
                          </Button>
                        </PermissionGate>
                        <PermissionGate permission="warehouse_edit">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7"
                            onClick={() => void rejectSku(approval)}
                          >
                            Remove from Review
                          </Button>
                        </PermissionGate>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <PaginationBar {...pendingSkuApprovalsPage} />
            </div>
          )}

          {discrepanciesCount === 0 && pendingSkuApprovalsCount === 0 ? (
            <Card><CardContent className="py-8 text-center text-muted-foreground text-sm">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
              No issues reported
            </CardContent></Card>
          ) : (
            discrepanciesPage.paged.map((d) => (
              <Card key={d.id} className="border-l-4 border-l-destructive">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      <span className="text-sm font-medium">{d.productName}</span>
                    </div>
                    <Badge variant="destructive" className="text-[10px]">{d.discrepancy!.type}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    <span className="font-mono">{d.sku}</span> · {d.vendorName} · {formatPoNumber(d.poNumber)} · {displayJobRef(d.jobId, d.jobLabel)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Expected: {d.expectedQuantity} · Received: {d.receivedQuantity}
                  </p>
                  {d.discrepancy!.notes && (
                    <p className="text-xs mt-1 bg-destructive/5 rounded p-1.5">{d.discrepancy!.notes}</p>
                  )}
                </CardContent>
              </Card>
            ))
          )}
          <PaginationBar {...discrepanciesPage} />
        </TabsContent>

        {/* ── Stock Levels Tab ── */}
        <TabsContent value="stock">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Package className="h-4 w-4" /> Stock Levels
                  </CardTitle>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    On-hand inventory is backed by product registry stock quantities.
                  </p>
                </div>
                <Button
                  size="sm"
                  className="h-8 text-xs gap-1"
                  onClick={() => {
                    setInventoryImportPreview(null);
                    setInventoryImportSource(null);
                    setInventoryImportOpen(true);
                  }}
                >
                  <FileUp className="h-3.5 w-3.5" /> Import CSV
                </Button>
              </div>
              {recentInventoryImportAudits.length > 0 && (
                <div className="mt-3 rounded-md border border-border/70 bg-muted/20 p-2">
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1">
                    <History className="h-3 w-3" /> Recent import audit
                  </p>
                  <div className="space-y-1">
                    {recentInventoryImportAudits.map((event, index) => (
                      <div key={`${event.timestamp}-${index}`} className="flex items-center justify-between gap-3 text-[11px]">
                        <span className="truncate">
                          {String(event.payload?.fileName ?? "CSV import")} · {String(event.payload?.mode ?? "add")}
                        </span>
                        <span className="text-muted-foreground shrink-0">
                          {String(event.payload?.changedRows ?? 0)} changed · {formatStandardTimestamp(event.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardHeader>

            {/* ── Stock location sub-tabs ── */}
            {warehouseSummaries.length > 0 && (
              <div className="px-4 pb-3 pt-1 border-b flex items-center gap-1.5 flex-wrap">
                <button
                  onClick={() => setStockSubTab("all")}
                  className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
                    stockSubTab === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:bg-muted/50"
                  }`}
                >
                  All
                </button>
                {warehouseSummaries.map((wh) => (
                  <button
                    key={wh.warehouseId}
                    onClick={() => setStockSubTab(wh.warehouseId)}
                    className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
                      stockSubTab === wh.warehouseId
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    <Warehouse className="h-2.5 w-2.5" />
                    {wh.warehouseName}
                  </button>
                ))}
              </div>
            )}

            <div className="overflow-auto">
              {stockSubTab !== "all" && (
                stockLocationLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px]">Product</TableHead>
                        <TableHead className="text-[10px]">SKU</TableHead>
                        <TableHead className="text-[10px] text-right">Received</TableHead>
                        <TableHead className="text-[10px] text-right">Transferred In</TableHead>
                        <TableHead className="text-[10px] text-right">Transferred Out</TableHead>
                        <TableHead className="text-[10px] text-right">Available</TableHead>
                        <TableHead className="text-[10px]">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {locationStockPage.paged.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-xs text-center text-muted-foreground py-8">
                            No stock at this location
                          </TableCell>
                        </TableRow>
                      ) : (
                        locationStockPage.paged.map((item) => (
                          <TableRow key={item.sku}>
                            <TableCell className="text-xs font-medium">{item.productName}</TableCell>
                            <TableCell className="text-[10px] font-mono text-muted-foreground">{item.sku}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{item.receivedQty}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{item.transferredIn}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{item.transferredOut}</TableCell>
                            <TableCell className="text-xs text-right font-mono font-bold">{item.availableQuantity}</TableCell>
                            <TableCell>
                              {item.availableQuantity > 0
                                ? <Badge className="text-[9px] bg-success/15 text-success border-success/30">In Stock</Badge>
                                : <Badge variant="secondary" className="text-[9px]">No Stock</Badge>}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )
              )}
              {stockSubTab !== "all" && !stockLocationLoading && <PaginationBar {...locationStockPage} />}
              {stockSubTab === "all" && <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px]">Product</TableHead>
                    <TableHead className="text-[10px]">SKU</TableHead>
                    <TableHead className="text-[10px]">Namespace</TableHead>
                    <TableHead className="text-[10px] text-right">On Hand</TableHead>
                    <TableHead className="text-[10px] text-right">Reserved</TableHead>
                    <TableHead className="text-[10px] text-right">Allocated</TableHead>
                    <TableHead className="text-[10px] text-right">Incoming</TableHead>
                    <TableHead className="text-[10px] text-right">Available</TableHead>
                    <TableHead className="text-[10px] text-right">Reorder At</TableHead>
                    <TableHead className="text-[10px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stockPage.paged.map((s) => {
                    const isShort = s.availableStock < 0;
                    const isLow = !isShort && s.reorderThreshold > 0 && s.availableStock <= s.reorderThreshold;
                    const cls = resolveWarehouseSkuStatus(s.sku, registrySkuSet);
                    const hasReceiving = warehouseServerPaging
                      ? Boolean(s.hasReceived)
                      : receivingRecords.some((r) => r.sku === s.sku && (r.receivedQuantity > 0 || r.status === "Fully Received"));
                    const neverReceived = s.currentStock === 0 && !hasReceiving;
                    return (
                      <TableRow key={s.sku} className={isShort ? "bg-destructive/10" : isLow ? "bg-warning/10 dark:bg-warning/10" : ""}>
                        <TableCell className="text-xs font-medium">{s.productName}</TableCell>
                        <TableCell className="text-[10px] font-mono text-muted-foreground">{s.sku}</TableCell>
                        <TableCell><SkuStatusBadge status={cls} /></TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.currentStock}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.reservedStock}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.allocatedStock}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.incomingStock}</TableCell>
                        <TableCell className={`text-xs text-right font-mono font-bold ${isShort ? "text-destructive" : isLow ? "text-warning" : ""}`}>{s.availableStock}</TableCell>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground">{s.reorderThreshold || "—"}</TableCell>
                        <TableCell>
                          {isShort ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="destructive" className="text-[9px] gap-1">
                                  <AlertTriangle className="h-2.5 w-2.5" /> Over-reserved
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>Reservations exceed on-hand stock. Available is negative — receive more or release a reservation.</TooltipContent>
                            </Tooltip>
                          ) : neverReceived
                            ? <Badge variant="secondary" className="text-[9px]">No inventory received yet</Badge>
                            : isLow
                              ? <Badge className="text-[9px] bg-warning/15 text-warning border-warning/30">Low Stock</Badge>
                              : s.availableStock > 0
                                ? <Badge className="text-[9px] bg-success/15 text-success border-success/30">In Stock</Badge>
                                : <Badge variant="secondary" className="text-[9px]">No Stock</Badge>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>}
            </div>
            {stockSubTab === "all" && <PaginationBar {...stockPage} />}
          </Card>

          {lowStockCount > 0 && (
            <div className="mt-3">
              <h3 className="text-xs font-bold mb-2 flex items-center gap-1 text-warning">
                <AlertTriangle className="h-3.5 w-3.5" /> Low Stock Alerts ({lowStockCount})
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {lowStockPage.paged.map((s) => (
                  <Card key={s.sku} className="shadow-none border-warning/30">
                    <CardContent className="p-3">
                      <p className="text-xs font-medium">{s.productName}</p>
                      <p className="text-[10px] font-mono text-muted-foreground">{s.sku}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-warning font-bold">{s.availableStock} available</span>
                        <span className="text-[10px] text-muted-foreground">threshold: {s.reorderThreshold}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <PaginationBar {...lowStockPage} />
            </div>
          )}

        </TabsContent>

        {/* ── Hub Tab ── */}
        <TabsContent value="hub" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Warehouses</h3>
              <p className="text-xs text-muted-foreground">Manage warehouse locations used in ordering and transfers.</p>
            </div>
            <PermissionGate permission="warehouse_edit">
              <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setAddWarehouseOpen(true)}>
                <Plus className="h-3 w-3 mr-1.5" />
                Add Warehouse
              </Button>
            </PermissionGate>
          </div>

          {addWarehouseOpen && (
            <Card className="border-dashed">
              <CardContent className="p-3 space-y-2">
                <p className="text-xs font-semibold">New Warehouse</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[10px] text-muted-foreground font-medium">Name *</label>
                    <Input
                      value={newWhName}
                      onChange={(e) => setNewWhName(e.target.value)}
                      placeholder="e.g. Tampa Warehouse"
                      className="h-7 text-xs"
                      onKeyDown={(e) => e.key === "Enter" && handleAddWarehouse()}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] text-muted-foreground font-medium">Code (optional)</label>
                    <Input
                      value={newWhCode}
                      onChange={(e) => setNewWhCode(e.target.value.toUpperCase())}
                      placeholder="e.g. TPA"
                      className="h-7 text-xs font-mono"
                      onKeyDown={(e) => e.key === "Enter" && handleAddWarehouse()}
                    />
                  </div>
                </div>
                {/* Address row */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-muted-foreground font-medium">Address (optional)</label>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-5 text-[10px] px-1.5 text-primary"
                      onClick={() => setShowAddMap((v) => !v)}
                    >
                      {showAddMap ? "Hide Map" : "Pick on Map"}
                    </Button>
                  </div>
                  <Input
                    value={newWhAddress}
                    onChange={(e) => { setNewWhAddress(e.target.value); if (!e.target.value) { setNewWhLat(null); setNewWhLng(null); } }}
                    placeholder="Enter address or pick on map"
                    className="h-7 text-xs"
                  />
                  {mapGeocoding && (
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" /> Fetching address…
                    </p>
                  )}
                  {newWhLat !== null && newWhLng !== null && (
                    <p className="text-[10px] text-muted-foreground font-mono">
                      {newWhLat.toFixed(5)}, {newWhLng.toFixed(5)}
                    </p>
                  )}
                </div>
                {showAddMap && (
                  <WarehouseMapPicker
                    value={newWhLat !== null && newWhLng !== null ? { lat: newWhLat, lng: newWhLng } : null}
                    onChange={(pin: WarehousePin) => {
                      setNewWhLat(pin.lat);
                      setNewWhLng(pin.lng);
                      if (pin.address) setNewWhAddress(pin.address);
                    }}
                    onLoading={setMapGeocoding}
                  />
                )}
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 text-xs" onClick={handleAddWarehouse} disabled={!newWhName.trim() || addingWarehouse}>
                    {addingWarehouse ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
                    setAddWarehouseOpen(false);
                    setNewWhName(""); setNewWhCode(""); setNewWhAddress("");
                    setNewWhLat(null); setNewWhLng(null); setShowAddMap(false);
                  }}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {warehousesLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-4 justify-center">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading warehouses…
            </div>
          ) : warehouseSummaries.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No warehouses added yet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {warehouseSummaries.map((wh) => (
                <Card key={wh.warehouseId} className={wh.isActive ? "" : "opacity-50"}>
                  <CardContent className="p-3">
                    {editingWarehouse?.warehouseId === wh.warehouseId ? (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground font-medium">Name *</label>
                            <Input value={editWhName} onChange={(e) => setEditWhName(e.target.value)} className="h-7 text-xs" />
                          </div>
                          <div className="space-y-1">
                            <label className="text-[10px] text-muted-foreground font-medium">Code</label>
                            <Input value={editWhCode} onChange={(e) => setEditWhCode(e.target.value.toUpperCase())} className="h-7 text-xs font-mono" />
                          </div>
                        </div>
                        {/* Address row */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <label className="text-[10px] text-muted-foreground font-medium">Address (optional)</label>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-5 text-[10px] px-1.5 text-primary"
                              onClick={() => setShowEditMap((v) => !v)}
                            >
                              {showEditMap ? "Hide Map" : "Pick on Map"}
                            </Button>
                          </div>
                          <Input
                            value={editWhAddress}
                            onChange={(e) => { setEditWhAddress(e.target.value); if (!e.target.value) { setEditWhLat(null); setEditWhLng(null); } }}
                            placeholder="Enter address or pick on map"
                            className="h-7 text-xs"
                          />
                          {editWhLat !== null && editWhLng !== null && (
                            <p className="text-[10px] text-muted-foreground font-mono">
                              {editWhLat.toFixed(5)}, {editWhLng.toFixed(5)}
                            </p>
                          )}
                        </div>
                        {showEditMap && (
                          <WarehouseMapPicker
                            value={editWhLat !== null && editWhLng !== null ? { lat: editWhLat, lng: editWhLng } : null}
                            onChange={(pin: WarehousePin) => {
                              setEditWhLat(pin.lat);
                              setEditWhLng(pin.lng);
                              if (pin.address) setEditWhAddress(pin.address);
                            }}
                            onLoading={() => {}}
                          />
                        )}
                        <div className="flex gap-2">
                          <Button size="sm" className="h-7 text-xs" onClick={handleSaveEditWarehouse} disabled={!editWhName.trim()}>Save</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditingWarehouse(null); setShowEditMap(false); }}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <Warehouse className="h-4 w-4 text-muted-foreground shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-semibold truncate">{wh.warehouseName}</p>
                              {wh.warehouseCode && <span className="text-[10px] font-mono text-muted-foreground">{wh.warehouseCode}</span>}
                              {!wh.isActive && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
                            </div>
                            <p className="text-[10px] text-muted-foreground">
                              {wh.skuCount} SKU{wh.skuCount !== 1 ? "s" : ""} · {wh.totalAvailableUnits} units available
                            </p>
                            {(() => {
                              const addr = warehousesList.find((w) => w.id === wh.warehouseId)?.address;
                              return addr ? <p className="text-[10px] text-muted-foreground truncate">{addr}</p> : null;
                            })()}
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-[10px] text-primary"
                              onClick={() => handleViewWarehouseStock(wh.warehouseId)}
                            >
                              <ChevronRight className={`h-3 w-3 mr-1 transition-transform ${viewingStockWarehouseId === wh.warehouseId ? "rotate-90" : ""}`} />
                              Stock
                            </Button>
                            <PermissionGate permission="warehouse_edit">
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                                const detail = warehousesList.find((w) => w.id === wh.warehouseId);
                                setEditingWarehouse(wh);
                                setEditWhName(wh.warehouseName);
                                setEditWhCode(wh.warehouseCode ?? "");
                                setEditWhAddress(detail?.address ?? "");
                                setEditWhLat(detail?.latitude ?? null);
                                setEditWhLng(detail?.longitude ?? null);
                                setShowEditMap(false);
                              }}>
                                <Pencil className="h-3 w-3" />
                              </Button>
                              {wh.isActive && (
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground" onClick={() => handleDeactivateWarehouse(wh.warehouseId, wh.warehouseName)}>
                                  <Power className="h-3 w-3" />
                                </Button>
                              )}
                            </PermissionGate>
                          </div>
                        </div>

                        {/* Inline stock view */}
                        {viewingStockWarehouseId === wh.warehouseId && (
                          <div className="border rounded-md overflow-hidden mt-1">
                            {warehouseStockLoading && !warehouseStockCache[wh.warehouseId] ? (
                              <div className="flex items-center gap-2 text-xs text-muted-foreground p-3">
                                <Loader2 className="h-3 w-3 animate-spin" /> Loading stock…
                              </div>
                            ) : (warehouseStockCache[wh.warehouseId] ?? []).length === 0 ? (
                              <p className="text-[11px] text-muted-foreground p-3">No stock available at this warehouse.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead className="bg-muted/40">
                                  <tr>
                                    <th className="text-left px-2.5 py-1.5 font-medium text-[10px]">Product</th>
                                    <th className="text-left px-2.5 py-1.5 font-medium text-[10px]">SKU</th>
                                    <th className="text-right px-2.5 py-1.5 font-medium text-[10px]">Available</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {(warehouseStockCache[wh.warehouseId] ?? []).map((item) => (
                                    <tr key={item.sku} className="hover:bg-muted/20">
                                      <td className="px-2.5 py-1.5 truncate max-w-[140px]">{item.productName}</td>
                                      <td className="px-2.5 py-1.5 font-mono text-muted-foreground text-[10px]">{item.sku}</td>
                                      <td className="px-2.5 py-1.5 text-right tabular-nums font-semibold">{item.availableQuantity}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Transfers Tab ── */}
        <TabsContent value="transfers" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Stock Transfers</h3>
              <p className="text-xs text-muted-foreground">
                Move stock between warehouses. Stock updates immediately when a transfer is submitted.
              </p>
            </div>
            <PermissionGate permission="warehouse_edit">
              <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => setTransferModalOpen(true)}>
                <ArrowLeftRight className="h-3 w-3 mr-1.5" />
                Transfer Stock
              </Button>
            </PermissionGate>
          </div>

          {transfersLoading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading transfers…
            </div>
          ) : transfers.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <ArrowLeftRight className="h-8 w-8 opacity-20" />
              <p className="text-xs">No transfers yet. Use "Transfer Stock" to move materials between warehouses.</p>
            </div>
          ) : (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pb-1">Transfer Log</p>
              <div className="border rounded-lg overflow-hidden divide-y">
                {transfers.map((transfer) => {
                  const isExpanded = expandedTransferId === transfer.id;
                  return (
                    <div key={transfer.id}>
                      {/* Row */}
                      <div
                        className="grid grid-cols-[140px_1fr_80px_100px_90px] items-center px-3 py-2.5 hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => setExpandedTransferId(isExpanded ? null : transfer.id)}
                      >
                        {/* Ref */}
                        <div className="flex items-center gap-1.5">
                          <ChevronRight className={`h-3 w-3 text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                          <span className="text-xs font-mono font-semibold text-primary">{transfer.refNumber}</span>
                        </div>
                        {/* Route */}
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                          <span className="truncate font-medium text-foreground">{transfer.fromWarehouseName}</span>
                          <ArrowRight className="h-3 w-3 shrink-0" />
                          <span className="truncate font-medium text-foreground">{transfer.toWarehouseName}</span>
                        </div>
                        {/* Items */}
                        <div>
                          <span className="text-xs font-medium text-primary tabular-nums">
                            {transfer.items.length} SKU{transfer.items.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        {/* Status */}
                        <div>
                          <Badge
                            variant="outline"
                            className={
                              transfer.status === "Completed"
                                ? "text-[10px] text-green-700 border-green-300 bg-green-50 dark:bg-green-950 dark:text-green-400"
                                : "text-[10px] text-muted-foreground"
                            }
                          >
                            {transfer.status}
                          </Badge>
                        </div>
                        {/* Date */}
                        <div className="text-[11px] text-muted-foreground">
                          {new Date(transfer.createdAt).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="bg-muted/20 border-t px-4 py-3 space-y-2">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Transferred Materials</p>
                            <span className="text-[10px] text-muted-foreground">·</span>
                            <p className="text-[10px] text-muted-foreground">
                              {transfer.fromWarehouseName} → {transfer.toWarehouseName}
                            </p>
                          </div>
                          <div className="border rounded-md overflow-hidden bg-background">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/40">
                                <tr>
                                  <th className="text-left px-3 py-1.5 font-medium text-[11px]">Product</th>
                                  <th className="text-left px-3 py-1.5 font-medium text-[11px]">SKU</th>
                                  <th className="text-right px-3 py-1.5 font-medium text-[11px]">Qty Transferred</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {transfer.items.map((item) => (
                                  <tr key={item.id} className="hover:bg-muted/20">
                                    <td className="px-3 py-2">{item.productName}</td>
                                    <td className="px-3 py-2 font-mono text-muted-foreground">{item.sku}</td>
                                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{item.quantityTransferred}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {transfer.notes && (
                            <p className="text-[11px] text-muted-foreground italic">Note: {transfer.notes}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {transferCount > TRANSFER_PAGE_SIZE && (
                <div className="flex items-center justify-between text-xs text-muted-foreground pt-2">
                  <span>
                    Showing {transferPage * TRANSFER_PAGE_SIZE + 1}–{Math.min((transferPage + 1) * TRANSFER_PAGE_SIZE, transferCount)} of {transferCount}
                  </span>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={transferPage === 0} onClick={() => loadTransfers(transferPage - 1)}>
                      Previous
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={(transferPage + 1) * TRANSFER_PAGE_SIZE >= transferCount} onClick={() => loadTransfers(transferPage + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <TransferRequestModal
            open={transferModalOpen}
            onOpenChange={setTransferModalOpen}
            onCreated={() => { loadTransfers(0); loadWarehouseSummaries(); }}
          />
        </TabsContent>

        {/* ── Numbers Audit Tab ── */}
        <TabsContent value="audit" className="space-y-4">
          <StockAuditPanel />
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <Dialog open={inventoryImportOpen} onOpenChange={setInventoryImportOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle className="flex items-center gap-2">
                <FileUp className="h-4 w-4" /> Import Stock CSV
              </DialogTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1"
                onClick={downloadInventoryCsvTemplate}
              >
                <Download className="h-3.5 w-3.5" /> Download template
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[220px_1fr]">
              <div className="space-y-1.5">
                <Label className="text-xs">Import mode</Label>
                <Select
                  value={inventoryImportMode}
                  onValueChange={(value) => void repriceInventoryImportPreview(value as InventoryCsvImportMode)}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add to current stock</SelectItem>
                    <SelectItem value="overwrite">Overwrite current stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">CSV file</Label>
                <Input
                  type="file"
                  accept=".csv,text/csv"
                  className="h-9 text-xs"
                  disabled={inventoryImportBusy}
                  onChange={(event) => void handleInventoryCsvSelected(event.target.files?.[0] ?? null)}
                />
              </div>
            </div>

            <div className="rounded-md border bg-muted/20 p-3 text-[11px] text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Accepted columns</p>
              <p>
                Required: <span className="font-mono">quantity</span> plus either <span className="font-mono">sku</span> or an exact
                <span className="font-mono"> product_name</span> match.
                Optional: <span className="font-mono">product_name</span>, <span className="font-mono">vendor</span>,
                <span className="font-mono"> category</span>, <span className="font-mono">subcategory</span>,
                <span className="font-mono"> unit_of_measure</span>, <span className="font-mono">cost</span>,
                <span className="font-mono"> price</span>, <span className="font-mono">reorder_threshold</span>,
                <span className="font-mono"> notes</span>.
              </p>
              <p className="mt-1">
                If SKU is blank, the importer auto-matches one exact product name. Ambiguous or missing matches stay blocked until you choose a SKU manually.
                Add mode increases current stock by the CSV quantity. Overwrite mode replaces current stock with the CSV quantity.
              </p>
            </div>

            {inventoryImportPreview && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <Card className="shadow-none"><CardContent className="p-2 text-center"><p className="text-lg font-bold">{inventoryImportPreview.totalRows}</p><p className="text-[10px] text-muted-foreground">CSV rows</p></CardContent></Card>
                  <Card className="shadow-none border-success/30"><CardContent className="p-2 text-center"><p className="text-lg font-bold text-success">{inventoryImportSummary.created}</p><p className="text-[10px] text-muted-foreground">New SKUs</p></CardContent></Card>
                  <Card className="shadow-none border-primary/30"><CardContent className="p-2 text-center"><p className="text-lg font-bold text-primary">{inventoryImportSummary.updated}</p><p className="text-[10px] text-muted-foreground">Updates</p></CardContent></Card>
                  <Card className="shadow-none"><CardContent className="p-2 text-center"><p className="text-lg font-bold">{inventoryImportSummary.unchanged}</p><p className="text-[10px] text-muted-foreground">Unchanged</p></CardContent></Card>
                  <Card className="shadow-none border-destructive/30"><CardContent className="p-2 text-center"><p className="text-lg font-bold text-destructive">{inventoryImportSummary.invalid}</p><p className="text-[10px] text-muted-foreground">Invalid</p></CardContent></Card>
                </div>

                <div className="rounded-md border overflow-hidden">
                  <datalist id="warehouse-import-sku-options">
                    {(warehouseServerPaging ? stockPage.paged : stockRecords).map((record) => (
                      <option key={record.sku} value={record.sku}>
                        {record.productName}
                      </option>
                    ))}
                  </datalist>
                  <div className="overflow-auto max-h-[360px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[10px]">Row</TableHead>
                          <TableHead className="text-[10px]">SKU</TableHead>
                          <TableHead className="text-[10px]">Product</TableHead>
                          <TableHead className="text-[10px] text-right">Current</TableHead>
                          <TableHead className="text-[10px] text-right">CSV Qty</TableHead>
                          <TableHead className="text-[10px] text-right">Result</TableHead>
                          <TableHead className="text-[10px]">Change</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventoryImportPreviewRowsPage.paged.map((row) => (
                          <TableRow key={`${row.rowNumber}-${row.sku}`} className={row.action === "invalid" ? "bg-destructive/10" : ""}>
                            <TableCell className="text-[10px] font-mono">{row.rowNumber}</TableCell>
                            <TableCell className="text-[10px] font-mono">{row.sku}</TableCell>
                            <TableCell className="text-xs">
                              <div className="font-medium truncate max-w-[280px]">{row.productName}</div>
                              {row.matchNote && (
                                <div className="text-[10px] text-muted-foreground">{row.matchNote}</div>
                              )}
                              {row.duplicateCount && row.duplicateCount > 1 && (
                                <div className="text-[10px] text-warning">Merged {row.duplicateCount} duplicate CSV rows</div>
                              )}
                              {row.error && <div className="text-[10px] text-destructive">{row.error}</div>}
                              {row.action === "invalid" && row.error?.toLowerCase().includes("sku") && (
                                <div className="mt-2 flex min-w-[260px] items-center gap-2">
                                  <Input
                                    list="warehouse-import-sku-options"
                                    value={inventoryManualSkuByRow[row.rowNumber] ?? ""}
                                    onChange={(event) =>
                                      setInventoryManualSkuByRow((current) => ({
                                        ...current,
                                        [row.rowNumber]: event.target.value,
                                      }))
                                    }
                                    placeholder="Enter or select SKU"
                                    className="h-7 text-[11px]"
                                    disabled={inventoryImportBusy}
                                  />
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[11px]"
                                    disabled={inventoryImportBusy}
                                    onClick={() => void applyManualInventorySkuMatch(row)}
                                  >
                                    Match
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono">{row.previousQuantity}</TableCell>
                            <TableCell className="text-xs text-right font-mono">{row.importedQuantity}</TableCell>
                            <TableCell className="text-xs text-right font-mono font-bold">{row.nextQuantity}</TableCell>
                            <TableCell>
                              {row.action === "created" && <Badge className="text-[9px] bg-success/15 text-success border-success/30">Create</Badge>}
                              {row.action === "updated" && <Badge className="text-[9px] bg-primary/15 text-primary border-primary/30">Update</Badge>}
                              {row.action === "unchanged" && <Badge variant="secondary" className="text-[9px]">No change</Badge>}
                              {row.action === "invalid" && <Badge variant="destructive" className="text-[9px]">Invalid</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <PaginationBar {...inventoryImportPreviewRowsPage} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInventoryImportOpen(false)} disabled={inventoryImportBusy}>
              Cancel
            </Button>
            <Button
              onClick={() => void applyInventoryCsvImport()}
              disabled={inventoryImportBusy || !inventoryImportPreview || inventoryImportSummary.changed === 0}
            >
              {inventoryImportBusy ? "Working..." : `Apply ${inventoryImportSummary.changed} Change${inventoryImportSummary.changed === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ReceivingDialog record={selectedReceiving} open={showReceiving} onOpenChange={setShowReceiving} onConfirm={handleConfirmReceiving} />
      <ReAllocationModal
        open={reAllocRow !== null}
        onOpenChange={(o) => { if (!o) setReAllocRow(null); }}
        allocation={reAllocRow}
        onConfirm={handleReAllocate}
      />
      <PartialAllocateDialog
        record={selectedAllocate}
        open={showAllocate}
        onOpenChange={setShowAllocate}
        onAllocate={handleAllocateToJob}
        requiredQty={allocateContext?.requiredQty}
        alreadyAllocated={allocateContext?.alreadyAllocated}
      />
      <JobMaterialAllocateDialog
        row={partialAllocRow}
        open={partialAllocRow !== null}
        onOpenChange={(o) => { if (!o) setPartialAllocRow(null); }}
        onAllocate={allocateJobMaterialQty}
      />
      <ShipmentReceivingDialog shipment={selectedShipment} open={showShipmentReceiving} onOpenChange={setShowShipmentReceiving} onConfirm={handleShipmentReceiving} />

      {/* ── Mark as Delivered — date picker dialog ── */}
      <Dialog open={Boolean(deliverDateDialog)} onOpenChange={(open) => { if (!open) setDeliverDateDialog(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Delivered</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">{deliverDateDialog?.vendorName}</p>
              <p className="text-sm text-muted-foreground font-mono">{deliverDateDialog?.poNumber ? formatPoNumber(deliverDateDialog.poNumber) : ""}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deliver-date">Actual delivered date</Label>
              <Input
                id="deliver-date"
                type="date"
                value={deliverDateValue}
                onChange={(e) => setDeliverDateValue(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This will be saved as the delivered date for the shipment and purchase order.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliverDateDialog(null)}>Cancel</Button>
            <PermissionGate permission="warehouse_edit">
              <Button
                disabled={!deliverDateValue}
                onClick={async () => {
                  if (!deliverDateDialog) return;
                  const iso = localDateInputToIso(deliverDateValue);
                  const ok = await handleShipmentStatus(deliverDateDialog.id, "Delivered", iso ?? undefined);
                  if (ok) {
                    toast.success("Shipment marked as Delivered");
                    setDeliverDateDialog(null);
                  } else {
                    toast.error("Could not mark shipment as Delivered");
                  }
                }}
              >
                <Truck className="mr-2 h-4 w-4" />
                Mark Delivered
              </Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Numbers Audit Panel ─── */

type AuditRow = {
  sku: string;
  productName: string;
  warehouseStock: number;   // warehouse_stock.current_stock
  stockLevels: number;      // stock_levels.quantity_on_hand
  productRegistry: number;  // product_registry.stock_quantity
  allocatedQty: number;     // job_allocations (Reserved + Ready for Install)
  receivedQty: number;      // receiving_records (Fully Received)
  drift: boolean;
  repairing?: boolean;
  repaired?: boolean;
};

const AUDIT_PAGE_SIZE = 25;

function StockAuditPanel() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState<string | null>(null);
  const [repairingAll, setRepairingAll] = useState(false);
  const [page, setPage] = useState(1);

  const runAudit = async () => {
    setLoading(true);
    try {
      const [wsRes, slRes, prRes, allocRes, rrRes] = await Promise.all([
        supabase.from("warehouse_stock").select("sku, product_name, current_stock").order("sku"),
        supabase.from("stock_levels").select("sku, quantity_on_hand"),
        supabase.from("product_registry").select("sku, stock_quantity").eq("track_inventory", true),
        supabase.from("job_allocations").select("sku, quantity, status").in("status", ["Reserved", "Ready for Install"]),
        supabase.from("receiving_records").select("sku, received_quantity, status").eq("status", "Fully Received"),
      ]);

      const slBySku = new Map<string, number>();
      for (const r of slRes.data ?? []) slBySku.set(r.sku, r.quantity_on_hand ?? 0);

      const prBySku = new Map<string, number>();
      for (const r of prRes.data ?? []) prBySku.set(r.sku, r.stock_quantity ?? 0);

      const allocBySku = new Map<string, number>();
      for (const r of allocRes.data ?? []) {
        allocBySku.set(r.sku, (allocBySku.get(r.sku) ?? 0) + (r.quantity ?? 0));
      }

      const rrBySku = new Map<string, number>();
      for (const r of rrRes.data ?? []) {
        rrBySku.set(r.sku, (rrBySku.get(r.sku) ?? 0) + (r.received_quantity ?? 0));
      }

      const auditRows: AuditRow[] = (wsRes.data ?? []).map((ws) => {
        const warehouseStock = ws.current_stock ?? 0;
        const stockLevels = slBySku.get(ws.sku) ?? 0;
        const productRegistry = prBySku.get(ws.sku) ?? 0;
        const allocatedQty = allocBySku.get(ws.sku) ?? 0;
        const receivedQty = rrBySku.get(ws.sku) ?? 0;
        // Drift = warehouse_stock and stock_levels disagree with the source record total
        const drift = warehouseStock !== stockLevels || warehouseStock !== receivedQty;
        return {
          sku: ws.sku,
          productName: ws.product_name ?? ws.sku,
          warehouseStock,
          stockLevels,
          productRegistry,
          allocatedQty,
          receivedQty,
          drift,
        };
      });

      // Sort: drifted rows first, then by SKU
      auditRows.sort((a, b) => {
        if (a.drift !== b.drift) return a.drift ? -1 : 1;
        return a.sku.localeCompare(b.sku);
      });

      setRows(auditRows);
      setLastFetched(new Date().toLocaleTimeString());
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  const repairSku = async (sku: string) => {
    setRows((prev) => prev.map((r) => r.sku === sku ? { ...r, repairing: true } : r));
    const result = await repairStockFromSourceRecords(sku);
    setRows((prev) => prev.map((r) => r.sku === sku ? { ...r, repairing: false, repaired: result.ok, drift: !result.ok } : r));
    if (result.ok) {
      toast.success(`Repaired ${sku}`);
    } else {
      toast.error(`Repair failed for ${sku} — check console`);
    }
  };

  const repairAll = async () => {
    const drifted = rows.filter((r) => r.drift && !r.repaired);
    if (drifted.length === 0) return;
    setRepairingAll(true);
    let fixed = 0;
    for (const row of drifted) {
      const result = await repairStockFromSourceRecords(row.sku);
      if (result.ok) {
        fixed++;
        setRows((prev) => prev.map((r) => r.sku === row.sku ? { ...r, repaired: true, drift: false } : r));
      }
    }
    setRepairingAll(false);
    toast.success(`Repaired ${fixed} of ${drifted.length} SKUs`);
  };

  const driftCount = rows.filter((r) => r.drift).length;
  const totalPages = Math.max(1, Math.ceil(rows.length / AUDIT_PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * AUDIT_PAGE_SIZE, page * AUDIT_PAGE_SIZE);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Numbers Audit</h3>
          <p className="text-xs text-muted-foreground">
            Compares stock quantities across tables against the source record total (receiving_records).
            Drifted SKUs show mismatched numbers — use Repair to recompute from source and fix all three tables at once.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {driftCount > 0 && (
            <Button size="sm" variant="destructive" className="text-xs h-8 gap-1.5" onClick={repairAll} disabled={repairingAll || loading}>
              {repairingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />}
              {repairingAll ? "Repairing…" : `Repair All (${driftCount})`}
            </Button>
          )}
          <Button size="sm" variant="outline" className="text-xs h-8 gap-1.5" onClick={runAudit} disabled={loading}>
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
            {loading ? "Auditing…" : "Run Audit"}
          </Button>
        </div>
      </div>

      {lastFetched && (
        <div className="flex items-center gap-3">
          <p className="text-[10px] text-muted-foreground">Last run: {lastFetched}</p>
          {driftCount > 0 ? (
            <Badge className="text-[9px] bg-destructive/15 text-destructive border-destructive/30">
              {driftCount} SKU{driftCount === 1 ? "" : "s"} with drift
            </Badge>
          ) : rows.length > 0 ? (
            <Badge className="text-[9px] bg-success/15 text-success border-success/30">All numbers match</Badge>
          ) : null}
        </div>
      )}

      <div className="rounded-md border bg-muted/20 p-3 text-[11px] text-muted-foreground">
        <p className="font-semibold text-foreground mb-1">What causes drift?</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li><strong>Silent sync skip</strong> — if <code>inventory_items</code> row is missing, warehouse_stock and stock_levels updates are skipped silently while product_registry updates succeed</li>
          <li><strong>Delta double-apply</strong> — before the fix, reconciliation ran twice per receive, applying the reserved delta twice</li>
          <li><strong>No rollback</strong> — the three tables update independently with no transaction; a partial failure permanently diverges them</li>
        </ul>
        <p className="mt-1.5">Repair rewrites all three tables with absolute values computed from <code>receiving_records</code> + <code>job_allocations</code> — idempotent and always correct.</p>
      </div>

      {rows.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center h-40 border border-dashed rounded-lg text-center">
          <ShieldCheck className="h-8 w-8 text-muted-foreground/30 mb-2" />
          <p className="text-xs text-muted-foreground">Click "Run Audit" to compare stock numbers across all tables</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
        <div className="rounded-md border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="text-[10px] font-bold w-8"></TableHead>
                  <TableHead className="text-[10px]">SKU</TableHead>
                  <TableHead className="text-[10px]">Product</TableHead>
                  <TableHead className="text-[10px] text-right" title="warehouse_stock.current_stock">WH Stock</TableHead>
                  <TableHead className="text-[10px] text-right" title="stock_levels.quantity_on_hand">Stock Lvl</TableHead>
                  <TableHead className="text-[10px] text-right" title="product_registry.stock_quantity">Registry</TableHead>
                  <TableHead className="text-[10px] text-right" title="job_allocations (Reserved + Ready for Install)">Allocated</TableHead>
                  <TableHead className="text-[10px] text-right" title="receiving_records (Fully Received) — source of truth">Received ✓</TableHead>
                  <TableHead className="text-[10px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow
                    key={row.sku}
                    className={row.repaired ? "bg-success/[0.04]" : row.drift ? "bg-destructive/[0.04] border-l-2 border-l-destructive" : ""}
                  >
                    <TableCell className="text-center py-1.5">
                      {row.repaired ? (
                        <CheckCircle2 className="h-3 w-3 text-success mx-auto" />
                      ) : row.drift ? (
                        <AlertTriangle className="h-3 w-3 text-destructive mx-auto" />
                      ) : (
                        <CheckCircle2 className="h-3 w-3 text-success mx-auto opacity-40" />
                      )}
                    </TableCell>
                    <TableCell className="text-[10px] font-mono py-1.5">{row.sku}</TableCell>
                    <TableCell className="text-xs py-1.5 max-w-[160px] truncate">{row.productName}</TableCell>
                    <TableCell className={`text-[11px] tabular-nums text-right py-1.5 font-semibold ${!row.repaired && row.drift && row.warehouseStock !== row.receivedQty ? "text-destructive" : ""}`}>
                      {row.warehouseStock}
                    </TableCell>
                    <TableCell className={`text-[11px] tabular-nums text-right py-1.5 ${!row.repaired && row.drift && row.stockLevels !== row.warehouseStock ? "text-destructive font-semibold" : ""}`}>
                      {row.stockLevels}
                    </TableCell>
                    <TableCell className={`text-[11px] tabular-nums text-right py-1.5 ${!row.repaired && row.drift && row.productRegistry !== row.warehouseStock ? "text-warning font-semibold" : ""}`}>
                      {row.productRegistry}
                    </TableCell>
                    <TableCell className="text-[11px] tabular-nums text-right py-1.5 text-muted-foreground">{row.allocatedQty}</TableCell>
                    <TableCell className="text-[11px] tabular-nums text-right py-1.5 font-semibold">{row.receivedQty}</TableCell>
                    <TableCell className="py-1.5 text-right">
                      {row.drift && !row.repaired && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[10px] px-2 text-destructive hover:bg-destructive/10"
                          disabled={row.repairing}
                          onClick={() => repairSku(row.sku)}
                        >
                          {row.repairing ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : "Repair"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-1 pt-2">
            <p className="text-[10px] text-muted-foreground">
              {rows.length} SKU{rows.length === 1 ? "" : "s"} — page {page} of {totalPages}
            </p>
            <PaginationBar page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
        </>
      )}
    </div>
  );
}
